import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { connectLiveUpdates } from '../services/liveUpdates';
import RosterImportModal from '../components/RosterImportModal';
import {
  ArrowLeft,
  FileSpreadsheet,
  Users,
  Plus,
  Play,
  Square,
  Trash2,
  Eraser,
  ExternalLink,
  Activity,
  RefreshCw,
  Upload,
  ChevronRight,
  ChevronDown,
  User,
  Server,
  X,
  AlertCircle,
  AlertTriangle,
  FileCode,
  Folder,
  FolderOpen,
  File,
  Eye,
  RotateCcw,
  Code2,
  Send,
  CheckCircle2,
  Rewind,
  Copy,
  Check,
  Camera,
  GraduationCap,
  HardDrive,
  Clock,
  MessageSquare,
  Star,
  Search,
  Pencil,
  Cpu,
  Globe
} from 'lucide-react';
import { labs, students, facultyPod, studentPortal } from '../services/api';
import ActivityRewind from '../components/ActivityRewind';
import StarterCodeEditor from '../components/StarterCodeEditor';
import { LabImageCard, useImageCatalog } from '../components/ImagePicker';
import {
  matchEntryForLab,
  shouldAdoptSuggestedMemory,
  buildContainerConfigUpdate,
  formatMemory,
} from '../utils/imageCatalog';
import { buildFileTree, javaFileMovesNotice, missingReferencesNotice } from '../utils/fileUtils';
import { toDateTimeLocal, schedulePatch } from '../utils/labSchedule';
import { formatDistanceToNow } from 'date-fns';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Field, SwitchRow } from '../components/Dialog';
import ClipboardPolicyField from '../components/ClipboardPolicyField';
import D2LSessionReminderSettings from '../components/D2LSessionReminderSettings';
import { d2lReminderSettings, d2lReminderPatch } from '../utils/d2lSessionReminder';
import LabNameDialog from '../components/LabNameDialog';
import TestEnvironmentCard from '../components/TestEnvironmentCard';
import LabInsightsCard from '../components/LabInsightsCard';
import { countLabel, EMPTY_ENVIRONMENT_STATS, environmentStateLabel } from '../utils/environmentStats';
import EnvironmentStatsStatus, { AdditionalEnvironmentStates } from '../components/EnvironmentStatsStatus';
import useEnvironmentStats from '../hooks/useEnvironmentStats';

function invalidateLabViews(queryClient, id) {
  return Promise.all([['lab', id], ['labs'], ['containerStats']].map(queryKey =>
    queryClient.invalidateQueries({ queryKey })));
}

function LabDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [showFilePreview, setShowFilePreview] = useState(null);
  const [expandedStarterCode, setExpandedStarterCode] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const statsQuery = useEnvironmentStats();

  const { data: lab, isLoading, error } = useQuery({
    queryKey: ['lab', id],
    queryFn: () => labs.get(id),
    refetchInterval: 10000,
  });

  // Fetch starter code info
  const { data: starterCodeData } = useQuery({
    queryKey: ['lab-starter-code', id],
    queryFn: () => labs.getStarterFiles(id),
    enabled: !!lab,
  });

  const { data: operationData } = useQuery({
    queryKey: ['lab-container-operation', id],
    queryFn: () => labs.containerOperation(id),
    refetchInterval: 2000,
    retry: false,
  });
  const containerOperation = operationData?.operation || lab?.containerOperation;

  // WebSocket for live updates
  useEffect(() => {
    if (!id) return;
    
    const socket = connectLiveUpdates();
    socket.on('connect', () => socket.emit('subscribe:lab', id));
    
    socket.on('lab:containers', () => {
      queryClient.invalidateQueries({ queryKey: ['lab', id] });
    });
    
    socket.on('student:added', () => {
      queryClient.invalidateQueries({ queryKey: ['lab', id] });
    });
    
    return () => {
      socket.emit('unsubscribe:lab', id);
      socket.close();
    };
  }, [id, queryClient]);

  const startAllMutation = useMutation({
    mutationFn: () => labs.start(id),
    onSuccess: async (data) => {
      await invalidateLabViews(queryClient, id);
      const msg = data.stats?.recreated > 0
        ? `✓ Started ${data.stats?.started || 0}, recreated ${data.stats?.recreated || 0} containers`
        : `✓ Started ${data.stats?.started || 0} containers`;
      alert(msg + (data.stats?.failed > 0 ? `\n⚠️ ${data.stats.failed} failed` : ''));
    },
    onError: (err) => {
      alert(`Failed to start containers: ${err.response?.data?.error || err.message}`);
    }
  });

  const stopAllMutation = useMutation({
    mutationFn: () => labs.stop(id),
    onSuccess: async (data) => {
      await invalidateLabViews(queryClient, id);
      alert(`✓ Stopped ${data.stats?.stopped || 0} containers` + 
        (data.stats?.failed > 0 ? `\n⚠️ ${data.stats.failed} failed` : ''));
    },
    onError: (err) => {
      alert(`Failed to stop containers: ${err.response?.data?.error || err.message}`);
    }
  });

  const recreateAllMutation = useMutation({
    mutationFn: () => labs.recreateAll(id),
    onSuccess: async (data) => {
      await invalidateLabViews(queryClient, id);
      alert(`${data.message}\n\nNote: Starter code will be copied in the background.`);
    },
    onError: (err) => {
      alert(`Failed to recreate containers: ${err.response?.data?.error || err.message}`);
    }
  });

  const bulkBusy = ['running', 'cancelling'].includes(containerOperation?.status)
    || startAllMutation.isPending || stopAllMutation.isPending || recreateAllMutation.isPending;

  const deleteMutation = useMutation({
    mutationFn: () => labs.delete(id, { removeContainers: true, removeVolumes: true }),
    onSuccess: () => navigate('/labs'),
  });

  const updateNameMutation = useMutation({
    mutationFn: (naming) => labs.update(id, naming),
    onSuccess: () => {
      invalidateLabViews(queryClient, id);
      for (const key of ['studentLab', 'studentLabs', 'section-labs', 'analytics-workspace']) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      setIsEditingName(false);
    },
  });

  const removeAllStudentsMutation = useMutation({
    mutationFn: () => labs.removeAllStudents(id, { removeContainers: true, removeVolumes: true }),
    onSuccess: () => invalidateLabViews(queryClient, id),
  });

  const studentsList = useMemo(() => {
    return [...(lab?.students || [])].sort((a, b) => {
      const nameA = (a.student_name || a.name || '').toLowerCase();
      const nameB = (b.student_name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [lab?.students]);

  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return studentsList;
    const term = searchTerm.toLowerCase().trim();
    return studentsList.filter(s =>
      (s.student_id || '').toLowerCase().includes(term) ||
      (s.student_name || s.name || '').toLowerCase().includes(term) ||
      (s.student_email || '').toLowerCase().includes(term)
    );
  }, [studentsList, searchTerm]);

  if (isLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }} role="status" aria-label="Loading lab details">
        <div className="spinner" style={{ margin: '0 auto', width: 40, height: 40 }} aria-hidden="true" />
        <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Loading lab details...</span>
      </div>
    );
  }

  if (error || !lab) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center' }} role="alert">
        <AlertCircle size={48} style={{ color: 'var(--accent-danger)', marginBottom: 16 }} aria-hidden="true" />
        <h2>Lab not found</h2>
        <Link to="/labs" className="btn btn-primary" style={{ marginTop: 16 }}>
          Back to Labs
        </Link>
      </div>
    );
  }

  const stats = statsQuery.data?.byLab?.[id] || EMPTY_ENVIRONMENT_STATS;
  const toggleStudent = (studentId) => {
    console.log('toggleStudent called with:', studentId);
    setExpandedStudents(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(studentId)) {
        newExpanded.delete(studentId);
      } else {
        newExpanded.add(studentId);
      }
      console.log('New expanded set:', [...newExpanded]);
      return newExpanded;
    });
  };

  return (
    <div className="animate-fade-in">
      {isEditingName && (
        <LabNameDialog lab={lab} onClose={() => setIsEditingName(false)}
          onSave={naming => updateNameMutation.mutate(naming)} pending={updateNameMutation.isPending}
          error={updateNameMutation.error?.response?.data?.message || updateNameMutation.error?.response?.data?.error || updateNameMutation.error?.message} />
      )}
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link 
          to="/labs" 
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 8, 
            color: 'var(--text-secondary)',
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          <ArrowLeft size={16} />
          Back to Labs
        </Link>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <h1 style={{ marginBottom: 0, overflowWrap: 'anywhere' }}>{lab.name}</h1>
              <button
                onClick={() => { updateNameMutation.reset(); setIsEditingName(true); }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: 'var(--text-muted)',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                }}
                title="Edit lab name"
                aria-label="Edit lab name"
                onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <Pencil size={16} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <code style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                background: 'var(--bg-tertiary)',
                padding: '4px 8px',
                borderRadius: 4,
              }}>
                {lab.name}
              </code>

              {/* Who owns this lab. Already in the payload as createdBy; shown
                  here so the instructor is identifiable without leaving the page. */}
              {lab.createdBy && (
                <span
                  title={lab.createdBy.email || undefined}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 13, color: 'var(--text-secondary)',
                    background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
                    padding: '4px 10px', borderRadius: 999,
                  }}
                >
                  <User size={13} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />
                  <span style={{ color: 'var(--text-muted)' }}>Instructor</span>
                  <strong style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    {lab.createdBy.name || lab.createdBy.email || 'Unassigned'}
                  </strong>
                  {lab.createdBy.department && (
                    <span style={{ color: 'var(--text-muted)' }}>· {lab.createdBy.department}</span>
                  )}
                </span>
              )}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              className="btn btn-secondary"
              onClick={() => invalidateLabViews(queryClient, id)}
              title="Refresh data"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                if (window.confirm('This will recreate all stopped/missing containers. Continue?')) {
                  recreateAllMutation.mutate();
                }
              }}
              disabled={bulkBusy || stats.total === 0}
              title="Recreate all missing/stopped containers"
              style={{ minWidth: 110 }}
            >
              {recreateAllMutation.isPending ? (
                <>
                  <div className="spinner" style={{ width: 16, height: 16 }} />
                  Recreating...
                </>
              ) : (
                <>
                  <RotateCcw size={16} />
                  Recreate
                </>
              )}
            </button>
            {stats.running > 0 ? (
              <button 
                className="btn btn-secondary"
                onClick={() => stopAllMutation.mutate()}
                disabled={bulkBusy}
                style={{ minWidth: 100 }}
              >
                {stopAllMutation.isPending ? (
                  <>
                    <div className="spinner" style={{ width: 16, height: 16 }} />
                    Stopping...
                  </>
                ) : (
                  <>
                    <Square size={16} />
                    Stop All
                  </>
                )}
              </button>
            ) : (
              <button 
                className="btn btn-success"
                onClick={() => startAllMutation.mutate()}
                disabled={bulkBusy || stats.total === 0}
                style={{ minWidth: 100 }}
              >
                {startAllMutation.isPending ? (
                  <>
                    <div className="spinner" style={{ width: 16, height: 16 }} />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Start All
                  </>
                )}
              </button>
            )}
            <button
              className="btn btn-danger"
              onClick={() => {
                if (window.confirm(`Delete lab "${lab.name}" and all student data?`)) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending || bulkBusy}
              aria-label="Delete lab"
              title="Delete lab"
            >
              {deleteMutation.isPending ? (
                <div className="spinner" style={{ width: 16, height: 16 }} aria-hidden="true" />
              ) : (
                <Trash2 size={16} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {containerOperation && (
        <div className="card" role={['failed', 'interrupted'].includes(containerOperation.status) ? 'alert' : 'status'}
          aria-live="polite" style={{ padding: 16, marginBottom: 16 }}>
          <strong>{({ start: 'Start containers', recreate: 'Recreate containers', stop: 'Stop containers' })[containerOperation.kind]}: </strong>
          {containerOperation.processed} / {containerOperation.total} processed — {containerOperation.status}.
          {containerOperation.error && <div>{containerOperation.error}</div>}
          {containerOperation.result?.message && <div>{containerOperation.result.message}</div>}
          {bulkBusy && <div>Progress is saved on the server; refreshing this page keeps the current operation.</div>}
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}>
        <StatCard icon={Users} label="Students" value={studentsList.length} color="var(--accent-primary)" />
        <StatCard icon={Server} label="Registered environments" value={countLabel(stats.total)} color="var(--accent-purple)" />
        <StatCard icon={Activity} label="Running environments" value={countLabel(stats.running)} color="var(--accent-success)" />
        <StatCard icon={Square} label="Stopped environments" value={countLabel(stats.stopped)} color="var(--accent-warning)" />
      </div>
      <AdditionalEnvironmentStates stats={stats} />
      <EnvironmentStatsStatus stats={stats} error={statsQuery.isError} loading={statsQuery.isPending} />
      {stats.unrostered > 0 && <p role="status" className="text-muted">
        {stats.unrostered} registered environment(s) belong to students outside this lab's roster and are included in these counts.
      </p>}

      {/* Test as a student: the staff member's own test environment */}
      <TestEnvironmentCard labId={id} />

      {/* Lab insights: how the students worked, where they got stuck */}
      <LabInsightsCard labId={id} />

      {/* Lab Settings Section */}
      <LabSettingsSection
        lab={lab}
        labId={id}
        onUpdate={() => invalidateLabViews(queryClient, id)}
      />

      {/* Faculty Pod Section */}
      <FacultyPodSection
        labId={id}
        lab={lab}
        onUpdate={() => invalidateLabViews(queryClient, id)}
      />

      {/* Starter Code Section */}
      <StarterCodeSection 
        labId={id}
        starterCodeData={starterCodeData}
        students={studentsList.filter(s => s.container)}
        studentsCount={studentsList.filter(s => s.container).length}
        onPreviewFile={(file) => setShowFilePreview(file)}
        expanded={expandedStarterCode}
        onToggleExpanded={() => setExpandedStarterCode(!expandedStarterCode)}
      />

      {/* Students tree */}
      <div className="card">
        <div className="card-header">
          <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={20} />
            Students ({searchTerm ? `${filteredStudents.length} / ${studentsList.length}` : studentsList.length})
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkAdd(true)}>
              <Upload size={14} />
              Bulk Add
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={removeAllStudentsMutation.isPending || studentsList.length === 0}
              onClick={() => {
                if (studentsList.length === 0) return;
                if (window.confirm(`Remove all ${studentsList.length} students and delete their containers?`)) {
                  removeAllStudentsMutation.mutate();
                }
              }}
            >
              {removeAllStudentsMutation.isPending ? (
                <div className="spinner" style={{ width: 14, height: 14 }} />
              ) : (
                <Trash2 size={14} />
              )}
              Remove All
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddStudent(true)}>
              <Plus size={14} />
              Add Student
            </button>
          </div>
        </div>

        {studentsList.length > 0 && (
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search by name, ID, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search students"
              style={{
                flex: 1,
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-primary)',
                borderRadius: 6,
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: 14,
                outline: 'none',
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: 'var(--text-muted)',
                  flexShrink: 0,
                }}
                title="Clear search"
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {studentsList.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            <Users size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
            <p>No students added yet</p>
            <button
              className="btn btn-primary"
              style={{ marginTop: 16 }}
              onClick={() => setShowAddStudent(true)}
            >
              <Plus size={16} />
              Add First Student
            </button>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            <Search size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
            <p>No students match "{searchTerm}"</p>
          </div>
        ) : (
          <div className="tree">
            {filteredStudents.map(student => (
              <StudentTreeItem
                key={student.student_id}
                student={student}
                labId={id}
                labName={lab.name}
                servesPreview={lab.containerConfig?.servesPreview === true}
                expanded={expandedStudents.has(student.student_id)}
                onToggle={() => toggleStudent(student.student_id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add student modal */}
      {showAddStudent && (
        <AddStudentModal
          labId={id}
          onClose={() => setShowAddStudent(false)}
          onAdded={() => {
            setShowAddStudent(false);
            invalidateLabViews(queryClient, id);
          }}
        />
      )}

      {/* Bulk add modal */}
      {showBulkAdd && (
        <BulkAddModal
          labId={id}
          onClose={() => setShowBulkAdd(false)}
          onAdded={() => {
            setShowBulkAdd(false);
            invalidateLabViews(queryClient, id);
          }}
        />
      )}

      {/* File preview modal */}
      {showFilePreview && (
        <FilePreviewModal
          labId={id}
          file={showFilePreview}
          onClose={() => setShowFilePreview(null)}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border-primary)',
      borderRadius: 8,
      padding: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <Icon size={20} style={{ color }} />
      <div>
        <div style={{ fontSize: 24, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
      </div>
    </div>
  );
}

// Exported so the per-student actions are tested at their REAL call site rather
// than through a harness copy, following the reasoning in LabDetail.test.js.
export function StudentTreeItem({ student, labId, labName, expanded, onToggle, servesPreview = false }) {
  const queryClient = useQueryClient();
  const [showRewind, setShowRewind] = useState(false);
  const container = student.container;
  const isRunning = container?.state === 'running';

  const startMutation = useMutation({
    mutationFn: () => students.start(labId, student.student_id),
    onSuccess: async (data) => {
      await invalidateLabViews(queryClient, labId);
      if (data.recreated) {
        alert(`✓ Container recreated and started for ${student.student_id}`);
      }
    },
    onError: (err) => {
      alert(`Failed to start container: ${err.response?.data?.error || err.message}`);
    }
  });

  const stopMutation = useMutation({
    mutationFn: () => students.stop(labId, student.student_id),
    onSuccess: async () => {
      await invalidateLabViews(queryClient, labId);
    },
    onError: (err) => {
      alert(`Failed to stop container: ${err.response?.data?.error || err.message}`);
    }
  });

  const removeMutation = useMutation({
    mutationFn: () => students.remove(labId, student.student_id, { removeContainer: true, removeVolumes: true }),
    onSuccess: async () => {
      await invalidateLabViews(queryClient, labId);
    },
  });

  const createContainerMutation = useMutation({
    mutationFn: () => students.createContainer(labId, student.student_id, {}),
    onSuccess: async () => {
      await invalidateLabViews(queryClient, labId);
    },
  });

  const openIdeMutation = useMutation({
    mutationFn: () => labs.authenticateStudentIDE(labId, student.student_id),
  });

  // The student's website preview (INFS2101), on its own origin. The tab is
  // opened on the click itself so no pop-up blocker stands in the way, then
  // sent to the signed-in address once the backend returns it.
  const openPreviewMutation = useMutation({
    mutationFn: () => studentPortal.openPreview(labId, { studentId: student.student_id }),
  });
  const openStudentPreview = async (event) => {
    event.stopPropagation();
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const { url } = await openPreviewMutation.mutateAsync();
      if (tab) tab.location.replace(url);
      else window.location.assign(url);
    } catch (error) {
      if (tab) tab.close();
      alert(error.response?.data?.error || 'Could not open the preview.');
    }
  };
  const openStudentIde = async (event) => {
    event.stopPropagation();
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const access = await openIdeMutation.mutateAsync();
      if (tab) tab.location.replace(access.url);
      else window.location.assign(access.url);
    } catch (error) {
      if (tab) tab.close();
      alert(error.response?.data?.error || 'Could not open the student IDE.');
    }
  };

  // --- Clear this student's workspace -------------------------------------
  //
  // Destructive and irreversible for whatever is in the workspace right now, so
  // the confirmation names the student and, more importantly, says whether a
  // snapshot exists. The snapshot line is what actually informs the decision,
  // so it is fetched when the dialog opens rather than with the page: the
  // student list can be 25+ rows and snapshot history is unbounded, and this
  // dialog opens for one student, rarely.
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const snapshotInfo = useQuery({
    queryKey: ['student-snapshots', labId, student.student_id],
    queryFn: () => facultyPod.listSnapshots(labId, { studentId: student.student_id }),
    enabled: clearConfirmOpen,
    staleTime: 30000,
  });

  const clearStarterMutation = useMutation({
    mutationFn: () => labs.clearStarterCodeForStudent(labId, student.student_id),
    onSuccess: () => {
      setClearConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['lab', labId] });
    },
    onError: (err) => {
      alert(`Failed to clear workspace: ${err.response?.data?.error || err.message}`);
    },
  });


  const reloadStarterMutation = useMutation({
    mutationFn: () => labs.reloadStarterCodeForStudent(labId, student.student_id),
    onSuccess: () => {
      alert(`Starter code reloaded for ${student.student_id}`);
    },
    onError: (err) => {
      alert(`Failed to reload starter code: ${err.response?.data?.error || err.message}`);
    },
  });

  return (
    <div style={{ marginBottom: 4 }}>
      <div
        className="tree-item"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${student.student_name || student.student_id}${container ? `, container ${environmentStateLabel(container.state).toLowerCase()}` : ', no container'}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          background: 'var(--bg-tertiary)',
          borderRadius: 8,
          borderLeft: `3px solid ${isRunning ? 'var(--accent-success)' : container ? 'var(--accent-warning)' : 'var(--border-primary)'}`,
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1,
        }}
        onClick={(e) => {
          console.log('Row clicked for student:', student.student_id);
          onToggle();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div style={{ padding: 0 }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
        
        <User size={18} style={{ color: 'var(--text-secondary)' }} />
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>{student.student_name || student.student_id}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {student.student_id}
            {student.student_email && ` • ${student.student_email}`}
          </div>
        </div>

        {container ? (
          <>
            <span className={`badge ${isRunning ? 'badge-success' : 'badge-warning'}`}>
              {environmentStateLabel(container.state)}
            </span>
            
            {isRunning && (container.url || container.port) && (
              <button
                className="btn btn-secondary btn-sm"
                title="Open IDE"
                aria-label={`Open IDE for ${student.student_name || student.student_id}`}
                onClick={openStudentIde}
                disabled={openIdeMutation.isPending}
              >
                <ExternalLink size={14} aria-hidden="true" />
              </button>
            )}

            {isRunning && servesPreview && (
              <button
                className="btn btn-secondary btn-sm"
                title="Open preview"
                aria-label={`Open preview for ${student.student_name || student.student_id}`}
                onClick={openStudentPreview}
                disabled={openPreviewMutation.isPending}
              >
                <Globe size={14} aria-hidden="true" />
              </button>
            )}

            {isRunning ? (
              <button
                className="btn btn-secondary btn-sm"
                onClick={(e) => { e.stopPropagation(); stopMutation.mutate(); }}
                disabled={stopMutation.isPending}
                title="Stop container"
                aria-label={`Stop container for ${student.student_name || student.student_id}`}
                style={{ minWidth: 32 }}
              >
                {stopMutation.isPending ? (
                  <div className="spinner" style={{ width: 14, height: 14 }} aria-hidden="true" />
                ) : (
                  <Square size={14} aria-hidden="true" />
                )}
              </button>
            ) : (
              <button
                className="btn btn-success btn-sm"
                onClick={(e) => { e.stopPropagation(); startMutation.mutate(); }}
                disabled={startMutation.isPending}
                title="Start container"
                aria-label={`Start container for ${student.student_name || student.student_id}`}
                style={{ minWidth: 32 }}
              >
                {startMutation.isPending ? (
                  <div className="spinner" style={{ width: 14, height: 14 }} aria-hidden="true" />
                ) : (
                  <Play size={14} aria-hidden="true" />
                )}
              </button>
            )}
          </>
        ) : (
          <button
            className="btn btn-primary btn-sm"
            onClick={(e) => { e.stopPropagation(); createContainerMutation.mutate(); }}
            disabled={createContainerMutation.isPending}
            style={{ minWidth: 140 }}
          >
            {createContainerMutation.isPending ? (
              <>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                Creating...
              </>
            ) : (
              <>
                <Server size={14} />
                Create Container
              </>
            )}
          </button>
        )}
        
        <button
          className="btn btn-icon btn-sm"
          style={{ color: 'var(--accent-danger)' }}
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Remove ${student.student_id} and their data?`)) {
              removeMutation.mutate();
            }
          }}
          title="Remove"
          aria-label={`Remove ${student.student_name || student.student_id}`}
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>

      {/* Expanded details */}
      {expanded && container && (
        <div style={{
          marginLeft: 24,
          marginTop: 4,
          padding: 16,
          background: 'var(--bg-secondary)',
          borderRadius: 8,
          fontSize: 13,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Container: </span>
              <code style={{ fontSize: 11 }}>{container.containerName || container.container_id?.slice(0, 12) || 'N/A'}</code>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Port: </span>
              <code>{container.port || 'N/A'}</code>
            </div>
            {container.url && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={{ color: 'var(--text-muted)' }}>URL: </span>
                <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{container.url}</code>
              </div>
            )}
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Status: </span>
              {container.status ? container.status.charAt(0).toUpperCase() + container.status.slice(1) : 'Unknown'}
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Last Accessed: </span>
              {container.lastAccessed ? formatDistanceToNow(new Date(container.lastAccessed), { addSuffix: true }) : 'Never'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {container.url && isRunning && (
              <button
                className="btn btn-primary btn-sm"
                onClick={openStudentIde}
                disabled={openIdeMutation.isPending}
              >
                <ExternalLink size={14} />
                Open Student IDE
              </button>
            )}
            <Link
              to={`/labs/${labId}/activity/${student.student_id}`}
              className="btn btn-secondary btn-sm"
              title="View activity logs and timeline"
            >
              <Activity size={14} />
              View Activity
            </Link>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowRewind(true)}
              title="Activity Rewind - View screenshot timeline"
              style={{ 
                background: 'linear-gradient(135deg, var(--accent-purple), var(--accent-secondary))',
                border: 'none',
                color: 'white',
              }}
            >
              <Rewind size={14} />
              Rewind
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => reloadStarterMutation.mutate()}
              disabled={reloadStarterMutation.isPending || !isRunning}
              title={isRunning ? "Reload starter code to this student's workspace" : "Container must be running"}
            >
              {reloadStarterMutation.isPending ? (
                <div className="spinner" style={{ width: 14, height: 14 }} />
              ) : (
                <Send size={14} />
              )}
              Reload Starter Code
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setClearConfirmOpen(true)}
              disabled={clearStarterMutation.isPending || !isRunning}
              title={isRunning
                ? "Delete every file in this student's workspace"
                : 'Container must be running'}
              aria-label={`Clear workspace for ${student.student_name || student.student_id}`}
            >
              <Eraser size={14} />
              Clear Workspace
            </button>
          </div>
        </div>
      )}
      
      {/* Activity Rewind Modal */}

      {clearConfirmOpen && (() => {
        // Snapshots are what make this recoverable, so say so plainly. Until the
        // lookup resolves we say "checking" rather than implying either answer.
        const snaps = snapshotInfo.data?.snapshots || [];
        const latest = snaps.length
          ? snaps.reduce((a, b) => (new Date(a.timestamp) > new Date(b.timestamp) ? a : b))
          : null;
        const loading = snapshotInfo.isLoading;
        const failed = snapshotInfo.isError;
        return (
          <Dialog size="sm" onClose={() => setClearConfirmOpen(false)} labelledBy="clear-ws-title" confirmOnClose={false}>
            <DialogHeader
              id="clear-ws-title"
              title="Clear workspace"
              icon={<Eraser size={18} aria-hidden="true" />}
              onClose={() => setClearConfirmOpen(false)}
            />
            <DialogBody>
              <p style={{ margin: '0 0 12px' }}>
                Delete every file in the workspace of{' '}
                <strong>{student.student_name || student.student_id}</strong>
                {student.student_name ? ` (${student.student_id})` : ''}?
              </p>
              <p style={{ margin: '0 0 14px', color: 'var(--text-secondary)', fontSize: 13 }}>
                The pod keeps running and <code>.labenv</code> is preserved, so screen
                recording and the audit trail are unaffected. The student starts again
                from an empty workspace.
              </p>

              <div
                role="status"
                style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  padding: '10px 12px', borderRadius: 6, fontSize: 13,
                  background: latest ? 'var(--badge-success-bg, var(--bg-tertiary))'
                    : loading ? 'var(--bg-tertiary)'
                      : 'var(--badge-warning-bg, var(--bg-tertiary))',
                }}
              >
                {latest
                  ? <Camera size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
                  : <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />}
                <span>
                  {loading && 'Checking for snapshots…'}
                  {failed && 'Could not check for snapshots — treat this as unrecoverable.'}
                  {!loading && !failed && latest && (
                    <>
                      Last snapshot{' '}
                      <strong>{formatDistanceToNow(new Date(latest.timestamp), { addSuffix: true })}</strong>
                      {snaps.length > 1 ? ` · ${snaps.length} versions kept` : ' · 1 version kept'}.
                      This work can be recovered from grading.
                    </>
                  )}
                  {!loading && !failed && !latest && (
                    <><strong>No snapshots for this student.</strong> Clearing cannot be undone.</>
                  )}
                </span>
              </div>
            </DialogBody>
            <DialogFooter spread>
              <button className="btn btn-secondary" onClick={() => setClearConfirmOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => clearStarterMutation.mutate()}
                disabled={clearStarterMutation.isPending || snapshotInfo.isLoading}
              >
                {clearStarterMutation.isPending ? 'Clearing…' : 'Clear workspace'}
              </button>
            </DialogFooter>
          </Dialog>
        );
      })()}

      {showRewind && (
        <div role="dialog" aria-modal="true" aria-label={`Activity rewind for ${student.student_name || student.student_id}`} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--modal-overlay-bg)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '1400px',
            height: '100%',
            maxHeight: '900px',
          }}>
            <ActivityRewind
              labId={labId}
              studentId={student.student_id}
              studentName={student.student_name || student.student_id}
              onClose={() => setShowRewind(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function AddStudentModal({ labId, onClose, onAdded }) {
  const [studentId, setStudentId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [createContainer, setCreateContainer] = useState(true);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const addMutation = useMutation({
    mutationFn: (data) => students.add(labId, data),
    onSuccess: () => onAdded(),
    onError: (err) => setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add student'),
  });

  // Validate student ID format
  const validateStudentId = (id) => {
    if (!id || id.length < 2) {
      return 'Student ID must be at least 2 characters';
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(id)) {
      return 'Student ID can only contain letters, numbers, underscore, or dash';
    }
    return '';
  };

  const handleStudentIdChange = (e) => {
    const value = e.target.value;
    setStudentId(value);
    if (value) {
      setValidationError(validateStudentId(value));
    } else {
      setValidationError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    // Frontend validation
    const idError = validateStudentId(studentId);
    if (idError) {
      setValidationError(idError);
      return;
    }
    
    addMutation.mutate({ studentId, name, email, createContainer });
  };

  return (
    <Dialog size="md" onClose={addMutation.isPending ? undefined : onClose} labelledBy="add-student-title">
      <DialogHeader id="add-student-title" title="Add Student" onClose={addMutation.isPending ? undefined : onClose} />

      <form className="dlg-form" onSubmit={handleSubmit} aria-label="Add student">
        <DialogBody>
          {error && (
            <div className="dlg-alert" role="alert">
              {error}
            </div>
          )}

          <Field label="Student ID" htmlFor="add-student-id" required>
            <input
              id="add-student-id"
              type="text"
              value={studentId}
              onChange={handleStudentIdChange}
              placeholder="e.g., alice, student001, 6012345678"
              required
              aria-required="true"
              aria-invalid={validationError ? 'true' : undefined}
              aria-describedby={validationError ? 'student-id-error' : 'student-id-hint'}
              style={{
                borderColor: validationError ? 'var(--accent-danger)' : undefined,
              }}
            />
            {validationError && (
              <div
                id="student-id-error"
                role="alert"
                style={{
                  color: 'var(--accent-danger)',
                  fontSize: 12,
                  marginTop: 4
                }}
              >
                {validationError}
              </div>
            )}
            <p id="student-id-hint" className="field-hint">
              Letters, numbers, underscore, or dash (min 2 chars)
            </p>
          </Field>

          <Field label="Name" htmlFor="add-student-name">
            <input
              id="add-student-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Student's full name"
            />
          </Field>

          <Field label="Email" htmlFor="add-student-email">
            <input
              id="add-student-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="student@university.edu"
            />
          </Field>

          <SwitchRow
            id="add-student-create-container"
            title="Create container immediately"
            checked={createContainer}
            onChange={(e) => setCreateContainer(e.target.checked)}
          />
        </DialogBody>

        <DialogFooter>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={addMutation.isPending}>
            {addMutation.isPending ? <div className="spinner" /> : 'Add Student'}
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

function BulkAddModal({ labId, onClose, onAdded }) {
  // Structured students straight from the PeopleSoft roster parser. The former
  // free-text box split each line on commas as "id, name, email" — but the
  // PeopleSoft Name column is "Surname,First", so pasting rows put the first
  // name into the email field and left the surname as the entire name. Importing
  // the file keeps the fields separate and derives the address from the ID.
  const [importedStudents, setImportedStudents] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [createContainers, setCreateContainers] = useState(true);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [failures, setFailures] = useState(null);
  const dismissButtonRef = useRef(null);

  const bulkMutation = useMutation({
    mutationFn: (data) => {
      return students.addBulk(labId, {
        ...data,
        onProgress: (progressData) => {
          setProgress(progressData);
        }
      });
    },
    onSuccess: (results) => {
      // If any per-student container creation failed, keep the modal open and
      // show the user which students failed so they can retry. Students were
      // still enrolled (the enrollment phase either succeeds for all or throws),
      // so onAdded() will be called on dismissal to refresh the parent list.
      if (results?.failed && results.failed.length > 0) {
        setFailures(results.failed);
        return;
      }
      setTimeout(() => {
        onAdded();
      }, 1000); // Small delay to show completion message
    },
    onError: (err) => {
      setError(err.response?.data?.error || err.message || 'Failed to add students');
      setProgress(null);
    },
  });

  // When the failures panel appears, the Submit button is removed from the DOM.
  // If it was the focused element, focus falls to <body>, which is bad for keyboard
  // users. Move focus to the Close button so the modal remains keyboard-navigable.
  useEffect(() => {
    if (failures && failures.length > 0 && dismissButtonRef.current) {
      dismissButtonRef.current.focus();
    }
  }, [failures]);

  const handleDismiss = () => {
    // Students were enrolled even if some containers failed, so refresh the list.
    if (failures && failures.length > 0) {
      onAdded();
    } else {
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setProgress(null);
    setFailures(null);
    
    if (importedStudents.length === 0) {
      setError('Import a class roster first.');
      return;
    }

    bulkMutation.mutate({ students: importedStudents, createContainers, parallelLimit: 50 });
  };

  const handleImport = (students) => {
    setShowImport(false);
    setError('');
    // Merge by studentId so importing twice cannot duplicate a row.
    setImportedStudents((prev) => {
      const byId = new Map(prev.map((s) => [s.studentId, s]));
      students.forEach((s) => byId.set(s.studentId, s));
      return [...byId.values()];
    });
  };

  return (
    <>
    {showImport && (
      <RosterImportModal onClose={() => setShowImport(false)} onImport={handleImport} />
    )}
    <Dialog
      size="lg"
      onClose={bulkMutation.isPending ? undefined : handleDismiss}
      labelledBy="bulk-add-title"
      isDirty={importedStudents.length > 0 && !failures}
    >
      <DialogHeader id="bulk-add-title" title="Bulk Add Students" onClose={bulkMutation.isPending ? undefined : handleDismiss} />

      <form className="dlg-form" onSubmit={handleSubmit} aria-label="Bulk add students">
        <DialogBody>
          {error && (
            <div className="dlg-alert" role="alert">
              {error}
            </div>
          )}

          <Field label="Class Roster">
            <p id="bulk-student-hint" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              Upload the PeopleSoft class roster export. Student IDs, names and UDST
              email addresses are read directly from the file.
            </p>

            <button
              type="button"
              className="btn"
              onClick={() => setShowImport(true)}
              disabled={bulkMutation.isPending}
              aria-describedby="bulk-student-hint"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
            >
              <FileSpreadsheet size={14} />
              {importedStudents.length > 0 ? 'Import a different roster' : 'Import from Excel'}
            </button>

            {importedStudents.length > 0 && (
              <div role="status" style={{
                marginTop: 12, padding: '10px 12px', background: 'var(--badge-info-bg)',
                border: '1px solid var(--accent-primary)', borderRadius: 6, fontSize: 13,
              }}>
                <strong>{importedStudents.length}</strong> student{importedStudents.length === 1 ? '' : 's'} ready to add
                <button
                  type="button"
                  className="btn"
                  onClick={() => setImportedStudents([])}
                  disabled={bulkMutation.isPending}
                  style={{ marginLeft: 12, fontSize: 12, padding: '4px 8px' }}
                >
                  Clear
                </button>
              </div>
            )}
          </Field>

          <SwitchRow
            id="bulk-create-containers"
            title="Create containers in parallel (faster)"
            checked={createContainers}
            onChange={(e) => setCreateContainers(e.target.checked)}
            disabled={bulkMutation.isPending}
          />

          {progress && (
            <div
              style={{
                marginBottom: 16,
                padding: 16,
                background: 'var(--bg-secondary)',
                borderRadius: 8,
                border: '1px solid var(--border-primary)'
              }}
              role="status"
              aria-live="polite"
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8
              }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  {progress.message || `${progress.stage}...`}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {progress.current}/{progress.total}
                </span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: 8,
                  background: 'var(--bg-tertiary)',
                  borderRadius: 4,
                  overflow: 'hidden'
                }}
                role="progressbar"
                aria-valuenow={progress.current}
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-label={`Adding students: ${progress.current} of ${progress.total}`}
              >
                <div style={{
                  width: `${(progress.current / progress.total) * 100}%`,
                  height: '100%',
                  background: progress.stage === 'complete' 
                    ? 'var(--accent-success)' 
                    : progress.stage === 'enrolled'
                    ? 'var(--accent-primary)'
                    : 'var(--accent-purple)',
                  transition: 'width 0.3s ease',
                  borderRadius: 4
                }} />
              </div>
              {progress.stage === 'complete' && !failures && (
                <div style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: 'var(--accent-success)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  <CheckCircle2 size={14} />
                  All done! Closing in a moment...
                </div>
              )}
            </div>
          )}

          {failures && failures.length > 0 && (
            <div
              role="alert"
              style={{
                marginBottom: 16,
                padding: 12,
                background: 'var(--btn-danger-bg)',
                borderRadius: 8,
                border: '1px solid var(--accent-danger)',
                color: 'var(--accent-danger)',
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Students were enrolled, but {failures.length} container{failures.length === 1 ? '' : 's'} failed to create:
              </div>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  maxHeight: 160,
                  overflowY: 'auto',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                }}
              >
                {failures.map((f) => (
                  <li key={f.studentId} style={{ marginBottom: 4, wordBreak: 'break-word' }}>
                    <strong>{f.studentId}</strong>: {f.error || 'Unknown error'}
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                You can create missing containers individually from the student list.
              </div>
            </div>
          )}

        </DialogBody>

        <DialogFooter>
          <button type="button" className="btn btn-secondary" onClick={handleDismiss} ref={dismissButtonRef}>
            {failures && failures.length > 0 ? 'Close' : 'Cancel'}
          </button>
          {!(failures && failures.length > 0) && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={bulkMutation.isPending || importedStudents.length === 0}
            >
              {bulkMutation.isPending ? (
                <>
                  <div className="spinner" />
                  Adding...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  {importedStudents.length > 0 ? `Add ${importedStudents.length} Student${importedStudents.length === 1 ? '' : 's'}` : 'Add Students'}
                </>
              )}
            </button>
          )}
        </DialogFooter>
      </form>
    </Dialog>
    </>
  );
}

// Which version of a lab's starter code (its uploadedAt, which every edit moves) had its
// note about links to missing files dismissed, kept in this browser: the note stays away
// across visits, and comes back once the starter code changes and links still do not match.
const dismissedLinksKey = (labId) => `starter-links-dismissed:${labId}`;
function dismissedLinksVersion(labId) {
  try {
    return localStorage.getItem(dismissedLinksKey(labId));
  } catch {
    return null;
  }
}

// Starter Code Section Component
export function StarterCodeSection({ labId, starterCodeData, students, studentsCount, onPreviewFile, expanded, onToggleExpanded }) {
  const queryClient = useQueryClient();
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [reloadProgress, setReloadProgress] = useState(null);
  const [clearProgress, setClearProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  // Java files the last upload moved to the folders their packages name.
  const [movesNotice, setMovesNotice] = useState(null);
  // The note about links to missing files, dismissed in this page ({ labId, version }); a
  // dismissal is also kept in this browser per lab (dismissedLinksVersion).
  const [linksDismissed, setLinksDismissed] = useState(null);
  const fileInputRef = useRef(null);

  // Upload starter code mutation
  const uploadMutation = useMutation({
    mutationFn: (file) => labs.uploadStarterCodeToLab(labId, file),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['lab-starter-code', labId] });
      invalidateLabViews(queryClient, labId);
      setIsUploading(false);
      setMovesNotice(javaFileMovesNotice(result?.javaFilesMoved));
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (err) => {
      setIsUploading(false);
      alert(`Failed to upload: ${err.response?.data?.error || err.message}`);
    },
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      alert('Please upload a ZIP file');
      return;
    }
    
    setIsUploading(true);
    setMovesNotice(null);
    uploadMutation.mutate(file);
  };

  const reloadAllMutation = useMutation({
    mutationFn: () => {
      const studentIds = students.map(s => s.student_id);
      return labs.reloadStarterCodeBulk(labId, {
        studentIds,
        parallelLimit: 50,
        onProgress: (progressData) => {
          setReloadProgress(progressData);
        }
      });
    },
    onSuccess: (results) => {
      setTimeout(() => {
        setReloadProgress(null);
        invalidateLabViews(queryClient, labId);
        alert(results.failed.length > 0
          ? `Reload finished with errors: ${results.success.length} succeeded, ${results.failed.length} FAILED. Check the affected students and retry.`
          : `Starter code reloaded for all ${results.success.length} student${results.success.length === 1 ? '' : 's'}.`);
      }, 1000);
    },
    onError: (err) => {
      setReloadProgress(null);
      alert(`Failed to reload: ${err.response?.data?.error || err.message}`);
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: () => {
      const studentIds = students.map(s => s.student_id);
      return labs.clearStarterCodeBulk(labId, {
        studentIds,
        parallelLimit: 50,
        onProgress: (progressData) => {
          setClearProgress(progressData);
        }
      });
    },
    onSuccess: (results) => {
      setTimeout(() => {
        setClearProgress(null);
        invalidateLabViews(queryClient, labId);
        alert(results.failed.length > 0
          ? `Clear finished with errors: ${results.success.length} cleared, ${results.failed.length} FAILED. Check the affected students and retry.`
          : `Workspaces cleared for all ${results.success.length} student${results.success.length === 1 ? '' : 's'}.`);
      }, 1000);
    },
    onError: (err) => {
      setClearProgress(null);
      alert(`Failed to clear: ${err.response?.data?.error || err.message}`);
    },
  });

  const removeAllStudentsMutation = useMutation({
    mutationFn: () => labs.removeAllStudents(labId, { removeContainers: true, removeVolumes: true }),
    onSuccess: () => {
      invalidateLabViews(queryClient, id);
    },
    onError: (err) => {
      alert(`Failed to remove: ${err.response?.data?.error || err.message}`);
    },
  });

  if (!starterCodeData) {
    return null;
  }

  const { hasStarterCode, files } = starterCodeData;
  // Links in the starter code's pages to files it does not have. Read from the starter
  // listing, so a lab uploaded before the check shows them too, and an edit in the
  // starter editor (which moves uploadedAt) updates them.
  const linksNotice = missingReferencesNotice(starterCodeData.missingReferences, starterCodeData.missingReferencesTotal);
  const starterVersion = String(starterCodeData.uploadedAt ?? '');
  const linksDismissedFor = linksDismissed?.labId === labId ? linksDismissed.version : dismissedLinksVersion(labId);
  const showLinksNotice = linksNotice && linksDismissedFor !== starterVersion;
  const dismissLinksNotice = () => {
    setLinksDismissed({ labId, version: starterVersion });
    try {
      localStorage.setItem(dismissedLinksKey(labId), starterVersion);
    } catch {
      // Storage unavailable: dismissed until the page is loaded again.
    }
  };

  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const renderTree = (node, path = '', depth = 0) => {
    const entries = Object.entries(node).sort(([, a], [, b]) => {
      const aIsFolder = !a._isFile;
      const bIsFolder = !b._isFile;
      if (aIsFolder && !bIsFolder) return -1;
      if (!aIsFolder && bIsFolder) return 1;
      return 0;
    });

    return entries.map(([name, item]) => {
      const fullPath = path ? `${path}/${name}` : name;
      const isFolder = !item._isFile;
      const isExpanded = expandedFolders.has(fullPath);

      return (
        <div key={fullPath}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              paddingLeft: depth * 16 + 8,
              borderRadius: 4,
              cursor: isFolder ? 'pointer' : 'default',
              fontSize: 13,
            }}
            onClick={() => isFolder && toggleFolder(fullPath)}
            onDoubleClick={() => !isFolder && onPreviewFile(fullPath)}
          >
            {isFolder ? (
              <>
                {isExpanded ? <FolderOpen size={14} style={{ color: 'var(--accent-warning)' }} /> : <Folder size={14} style={{ color: 'var(--accent-warning)' }} />}
                <span>{name}</span>
              </>
            ) : (
              <>
                <File size={14} style={{ color: 'var(--text-muted)' }} />
                <span style={{ flex: 1 }}>{name}</span>
                {item.size && (
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                    {item.size < 1024 ? `${item.size}B` : `${(item.size / 1024).toFixed(1)}KB`}
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); onPreviewFile(fullPath); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    color: 'var(--text-muted)',
                  }}
                  title="Preview file"
                >
                  <Eye size={12} />
                </button>
              </>
            )}
          </div>
          {isFolder && isExpanded && item._children && (
            <div>{renderTree(item._children, fullPath, depth + 1)}</div>
          )}
        </div>
      );
    });
  };

  const fileTree = hasStarterCode ? buildFileTree(files.filter(f => f.type === 'file')) : null;
  const fileCount = files?.filter(f => f.type === 'file').length || 0;

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div 
        className="card-header" 
        style={{ cursor: 'pointer' }}
        onClick={onToggleExpanded}
      >
        <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <Code2 size={20} />
          Starter Code
          {hasStarterCode && (
            <span className="badge badge-info" style={{ marginLeft: 8 }}>
              {fileCount} files
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasStarterCode && (
            <>
              <button
                className="btn btn-danger btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Clear all workspace files for ${studentsCount} student(s)? This removes all files except system files (.labenv). This cannot be undone.`)) {
                    clearAllMutation.mutate();
                  }
                }}
                disabled={clearAllMutation.isPending || reloadAllMutation.isPending || studentsCount === 0}
              >
                {clearAllMutation.isPending ? (
                  <div className="spinner" style={{ width: 14, height: 14 }} />
                ) : (
                  <Trash2 size={14} />
                )}
                Clear All ({studentsCount})
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  reloadAllMutation.mutate();
                }}
                disabled={reloadAllMutation.isPending || clearAllMutation.isPending || studentsCount === 0}
              >
                {reloadAllMutation.isPending ? (
                  <div className="spinner" style={{ width: 14, height: 14 }} />
                ) : (
                  <RotateCcw size={14} />
                )}
                Reload All ({studentsCount})
              </button>
            </>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              setShowEditor(true);
            }}
          >
            <Pencil size={14} />
            Edit
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            disabled={isUploading}
          >
            {isUploading ? (
              <div className="spinner" style={{ width: 14, height: 14 }} />
            ) : (
              <Upload size={14} />
            )}
            {hasStarterCode ? 'Update' : 'Upload'}
          </button>
        </div>
      </div>

      {reloadProgress && (
        <div style={{
          padding: 16,
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-primary)',
          borderBottom: '1px solid var(--border-primary)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 8
          }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {reloadProgress.message || `${reloadProgress.stage}...`}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {reloadProgress.current}/{reloadProgress.total}
            </span>
          </div>
          <div style={{
            width: '100%',
            height: 8,
            background: 'var(--bg-tertiary)',
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(reloadProgress.current / reloadProgress.total) * 100}%`,
              height: '100%',
              background: reloadProgress.stage === 'complete'
                ? (reloadProgress.failedCount > 0 ? 'var(--accent-danger)' : 'var(--accent-success)')
                : 'var(--accent-purple)',
              transition: 'width 0.3s ease',
              borderRadius: 4
            }} />
          </div>
          {reloadProgress.stage === 'complete' && (
            <div style={{
              marginTop: 8,
              fontSize: 12,
              color: reloadProgress.failedCount > 0 ? 'var(--accent-danger)' : 'var(--accent-success)',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              {reloadProgress.failedCount > 0 ? (
                <>
                  <AlertTriangle size={14} />
                  Completed with {reloadProgress.failedCount} failed ({reloadProgress.successCount} succeeded)
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} />
                  Reload complete!
                </>
              )}
            </div>
          )}
        </div>
      )}

      {clearProgress && (
        <div style={{
          padding: 16,
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-primary)',
          borderBottom: '1px solid var(--border-primary)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8
          }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {clearProgress.message || `${clearProgress.stage}...`}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {clearProgress.current}/{clearProgress.total}
            </span>
          </div>
          <div style={{
            width: '100%',
            height: 8,
            background: 'var(--bg-tertiary)',
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(clearProgress.current / clearProgress.total) * 100}%`,
              height: '100%',
              background: clearProgress.stage === 'complete'
                ? (clearProgress.failedCount > 0 ? 'var(--accent-danger)' : 'var(--accent-success)')
                : 'var(--accent-purple)',
              transition: 'width 0.3s ease',
              borderRadius: 4
            }} />
          </div>
          {clearProgress.stage === 'complete' && (
            <div style={{
              marginTop: 8,
              fontSize: 12,
              color: clearProgress.failedCount > 0 ? 'var(--accent-danger)' : 'var(--accent-success)',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              {clearProgress.failedCount > 0 ? (
                <>
                  <AlertTriangle size={14} />
                  Completed with {clearProgress.failedCount} failed ({clearProgress.successCount} succeeded)
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} />
                  Clear complete!
                </>
              )}
            </div>
          )}
        </div>
      )}

      {movesNotice && (
        <div role="status" style={{
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          marginBottom: 12,
          padding: '10px 14px',
          borderRadius: 8,
          fontSize: 13,
          background: 'var(--badge-info-bg)',
          border: '1px solid var(--accent-primary)',
        }}>
          <FolderOpen size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, color: 'var(--accent-primary)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div>{movesNotice.headline}</div>
            <ul style={{ margin: '6px 0', paddingLeft: 18, fontFamily: 'var(--font-mono)', fontSize: 12, overflowWrap: 'anywhere' }}>
              {movesNotice.moves.slice(0, 8).map((line) => <li key={line}>{line}</li>)}
              {movesNotice.moves.length > 8 && <li>and {movesNotice.moves.length - 8} more</li>}
            </ul>
            <div style={{ color: 'var(--text-secondary)' }}>{movesNotice.note}</div>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            aria-label="Dismiss the note about moved Java files"
            onClick={() => setMovesNotice(null)}
            style={{ flexShrink: 0 }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {showLinksNotice && (
        <div role="status" aria-label="Links to missing files" style={{
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          marginBottom: 12,
          padding: '10px 14px',
          borderRadius: 8,
          fontSize: 13,
          background: 'var(--badge-warning-bg)',
          border: '1px solid var(--accent-warning)',
        }}>
          <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, color: 'var(--accent-warning)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div>{linksNotice.headline}</div>
            <ul style={{ margin: '6px 0', paddingLeft: 18, overflowWrap: 'anywhere' }}>
              {linksNotice.lines.map((line, i) => <li key={i}>{line}</li>)}
              {linksNotice.more > 0 && <li>and {linksNotice.more} more</li>}
            </ul>
            <div style={{ color: 'var(--text-secondary)' }}>
              Fix them with Edit, or upload a corrected ZIP with Update. {linksNotice.note}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            aria-label="Dismiss the note about links to missing files"
            onClick={dismissLinksNotice}
            style={{ flexShrink: 0 }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Hidden file input for upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {expanded && (
        <>
          {!hasStarterCode ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
              <FileCode size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
              <p>No starter code configured for this lab</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, marginBottom: 16 }}>
                Upload a ZIP file containing the starter code for students
              </p>
              <button
                className="btn btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <div className="spinner" style={{ width: 16, height: 16 }} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload Starter Code
                  </>
                )}
              </button>
            </div>
          ) : (
            <div style={{ 
              background: 'var(--bg-tertiary)', 
              borderRadius: 8, 
              padding: 12,
              maxHeight: 300,
              overflow: 'auto',
              fontFamily: 'var(--font-mono)',
            }}>
              {renderTree(fileTree)}
            </div>
          )}
        </>
      )}

      {showEditor && (
        <StarterCodeEditor
          labId={labId}
          onClose={() => {
            setShowEditor(false);
            queryClient.invalidateQueries({ queryKey: ['lab-starter-code', labId] });
          }}
        />
      )}
    </div>
  );
}

// File Preview Modal
function FilePreviewModal({ labId, file, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['file-preview', labId, file],
    queryFn: () => labs.getStarterFile(labId, file),
  });

  return (
    <Dialog size="lg" onClose={onClose} confirmOnClose={false}>
      <DialogHeader title={file} icon={<File size={18} />} onClose={onClose} />

      <DialogBody>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : error ? (
          <div style={{ padding: 20, color: 'var(--accent-danger)' }}>
            Failed to load file: {error.message}
          </div>
        ) : data?.binary ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            <AlertCircle size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <p>Binary file ({(data.size / 1024).toFixed(1)} KB)</p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cannot preview binary files</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            {data?.truncated && (
              <div style={{
                padding: '8px 16px',
                background: 'var(--badge-warning-bg)',
                borderBottom: '1px solid var(--accent-warning)',
                fontSize: 12,
                color: 'var(--accent-warning)',
              }}>
                ⚠️ File truncated (showing first 100KB of {(data.size / 1024).toFixed(1)} KB)
              </div>
            )}
            <pre style={{
              background: 'var(--bg-tertiary)',
              padding: 16,
              margin: 0,
              overflow: 'auto',
              maxHeight: 500,
              fontSize: 13,
              lineHeight: 1.5,
              fontFamily: 'var(--font-mono)',
            }}>
              <code>{data?.content}</code>
            </pre>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </DialogFooter>
    </Dialog>
  );
}

// Shareable Links Component (D2L and Direct Access)
function D2LShareableLink({ labId, labName, requireD2LAccess }) {
  const [copiedD2L, setCopiedD2L] = useState(false);
  const [copiedDirect, setCopiedDirect] = useState(false);
  
  // Generate the shareable links
  const baseUrl = window.location.origin.replace(':3000', ':4000'); // Use backend URL
  const frontendUrl = window.location.origin;
  
  // D2L link: {Username} is a D2L replacement variable
  const d2lShareableLink = `${baseUrl}/api/student/launch?labId=${labId}&studentId={Username}`;
  
  // Direct student portal link (for non-D2L access)
  const directPortalLink = `${frontendUrl}/student-login?labId=${labId}`;
  
  const handleCopyD2L = async () => {
    try {
      await navigator.clipboard.writeText(d2lShareableLink);
      setCopiedD2L(true);
      setTimeout(() => setCopiedD2L(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleCopyDirect = async () => {
    try {
      await navigator.clipboard.writeText(directPortalLink);
      setCopiedDirect(true);
      setTimeout(() => setCopiedDirect(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div style={{ marginTop: 20 }}>
      {/* D2L Shareable Link */}
      <label style={{ 
        display: 'block',
        fontWeight: 500, 
        marginBottom: 8, 
        fontSize: 14,
        color: 'var(--text-primary)',
      }}>
        D2L Shareable Link
      </label>
      <p style={{ 
        fontSize: 12, 
        color: 'var(--text-muted)', 
        marginBottom: 10,
      }}>
        Students open this link in D2L to sign in automatically and go straight to their IDE in the same lab tab.
      </p>
      
      <div style={{
        display: 'flex',
        gap: 8,
        alignItems: 'stretch',
      }}>
        <div style={{
          flex: 1,
          padding: '10px 12px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          borderRadius: 6,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: 'var(--text-secondary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {d2lShareableLink}
        </div>
        <button
          onClick={handleCopyD2L}
          className="btn btn-secondary btn-sm"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
            background: copiedD2L ? 'var(--badge-success-bg)' : undefined,
            borderColor: copiedD2L ? 'var(--accent-success)' : undefined,
            color: copiedD2L ? 'var(--accent-success)' : undefined,
          }}
        >
          {copiedD2L ? (
            <>
              <Check size={14} />
              Copied!
            </>
          ) : (
            <>
              <Copy size={14} />
              Copy
            </>
          )}
        </button>
      </div>
      
      <div style={{
        marginTop: 12,
        padding: '10px 12px',
        background: 'var(--badge-warning-bg)',
        border: '1px solid var(--accent-warning)',
        borderRadius: 6,
        fontSize: 12,
      }}>
        <p style={{ color: 'var(--accent-warning)', fontWeight: 500, marginBottom: 4 }}>
          📝 How to use in D2L:
        </p>
        <ol style={{ color: 'var(--text-secondary)', margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>Copy the link above</li>
          <li>In D2L, create a Quicklink or paste in Quiz/Assignment</li>
          <li>Check with a student account that <code style={{ background: 'var(--bg-hover)', padding: '1px 4px', borderRadius: 3 }}>{'{Username}'}</code> is replaced with the student's ID</li>
          <li>Set the quiz link to open in a new tab; that tab becomes the IDE</li>
        </ol>
        <p style={{ color: 'var(--text-secondary)', marginTop: 10, marginBottom: 0 }}>
          <strong>LockDown Browser:</strong> in the quiz's Advanced Settings, enable “Allow access to specific external web domains”
          and add <code>{window.location.hostname}</code> (domain only). Put the link inside an active quiz question and test with a student account.
        </p>
      </div>
      
      {/* Direct Access Link (only shown when D2L is NOT required) */}
      {!requireD2LAccess && (
        <div style={{ marginTop: 20 }}>
          <label style={{ 
            display: 'block',
            fontWeight: 500, 
            marginBottom: 8, 
            fontSize: 14,
            color: 'var(--text-primary)',
          }}>
            📧 Direct Access Link (Email OTP)
          </label>
          <p style={{ 
            fontSize: 12, 
            color: 'var(--text-muted)', 
            marginBottom: 10,
          }}>
            Share this link with students for direct access without D2L. They'll verify via email OTP.
          </p>
          
          <div style={{
            display: 'flex',
            gap: 8,
            alignItems: 'stretch',
          }}>
            <div style={{
              flex: 1,
              padding: '10px 12px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-primary)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: 'var(--text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {directPortalLink}
            </div>
            <button
              onClick={handleCopyDirect}
              className="btn btn-secondary btn-sm"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                background: copiedDirect ? 'var(--badge-success-bg)' : undefined,
                borderColor: copiedDirect ? 'var(--accent-success)' : undefined,
                color: copiedDirect ? 'var(--accent-success)' : undefined,
              }}
            >
              {copiedDirect ? (
                <>
                  <Check size={14} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={14} />
                  Copy
                </>
              )}
            </button>
          </div>
          
          <div style={{
            marginTop: 12,
            padding: '10px 12px',
            background: 'var(--badge-info-bg)',
            border: '1px solid var(--accent-primary)',
            borderRadius: 6,
            fontSize: 12,
          }}>
            <p style={{ color: 'var(--accent-primary)', fontWeight: 500, marginBottom: 4 }}>
              🔑 Student Login Flow:
            </p>
            <ol style={{ color: 'var(--text-secondary)', margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
              <li>Student clicks the link</li>
              <li>Enters their Student ID</li>
              <li>Receives verification code via email</li>
              <li>Enters code to access their labs</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

// Lab Settings Section Component
// Exported for LabDetail.test.js. The dominant failure mode of the environment
// picker is an UNRELATED save rewriting what a live class runs, and that lives
// in handleSave below — not in the reusable picker component, which has its own
// tests against a harness. A harness cannot catch drift in this call site, so
// this section is rendered directly.
export function LabSettingsSection({ lab, labId, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const scheduleBaseline = useRef(lab);
  const [formData, setFormData] = useState({
    status: lab.status || 'draft',
    openDate: toDateTimeLocal(lab.openDate),
    dueDate: toDateTimeLocal(lab.dueDate),
    closeDate: toDateTimeLocal(lab.closeDate),
    autoCloseOnDeadline: lab.autoCloseOnDeadline ?? true,
    gracePeriodMinutes: lab.gracePeriodMinutes ?? 0,
    requireD2LAccess: lab.requireD2LAccess ?? true,
    d2lSessionReminder: d2lReminderSettings(lab.d2lSessionReminder),
    // Activity Rewind settings
    screenRecordingEnabled: lab.screenRecording?.enabled ?? true,
    screenRecordingIntervalMs: lab.screenRecording?.intervalMs ?? 500,
    // Workspace download
    allowWorkspaceDownload: lab.allowWorkspaceDownload ?? false,
    allowWorkspaceUpload: lab.allowWorkspaceUpload ?? false,
    clipboardPolicy: lab.clipboardPolicy || 'allow',
    allowAppPreview: lab.allowAppPreview === true,
    allowDesktop: lab.allowDesktop === true,
    allowStudentDocker: lab.allowStudentDocker === true,
    // Idle pause is on unless the lab turned it off.
    keepEnvironmentsRunning: lab.idleStop?.enabled === false,
    // Memory ceiling for student and faculty environments.
    memoryLimit: lab.containerConfig?.memoryLimit || '3Gi',
  });

  // Sync formData when lab data changes (fixes initial load showing wrong defaults)
  useEffect(() => {
    if (!isEditing && lab.id) {
      scheduleBaseline.current = lab;
      setFormData({
        status: lab.status || 'draft',
        openDate: toDateTimeLocal(lab.openDate),
        dueDate: toDateTimeLocal(lab.dueDate),
        closeDate: toDateTimeLocal(lab.closeDate),
        autoCloseOnDeadline: lab.autoCloseOnDeadline ?? true,
        gracePeriodMinutes: lab.gracePeriodMinutes ?? 0,
        requireD2LAccess: lab.requireD2LAccess ?? true,
        d2lSessionReminder: d2lReminderSettings(lab.d2lSessionReminder),
        screenRecordingEnabled: lab.screenRecording?.enabled ?? true,
        screenRecordingIntervalMs: lab.screenRecording?.intervalMs ?? 500,
        allowWorkspaceDownload: lab.allowWorkspaceDownload ?? false,
        allowWorkspaceUpload: lab.allowWorkspaceUpload ?? false,
        clipboardPolicy: lab.clipboardPolicy || 'allow',
        allowAppPreview: lab.allowAppPreview === true,
        allowDesktop: lab.allowDesktop === true,
        allowStudentDocker: lab.allowStudentDocker === true,
        keepEnvironmentsRunning: lab.idleStop?.enabled === false,
        memoryLimit: lab.containerConfig?.memoryLimit || '3Gi',
      });
    }
  }, [lab, isEditing]);

  // ---- Lab environment (container image) -------------------------------------------------
  // Held outside formData on purpose. The selection is an *entry*, not a string, because the
  // labs already in the database store the legacy unqualified 'lab-environment:latest' while
  // the catalogue publishes a fully-qualified ref for the same environment. Comparing entries
  // means re-picking the environment a lab already has is correctly a no-op, and the save
  // below then leaves containerConfig.image untouched.
  const imageCatalogState = useImageCatalog();
  const { entries: imageEntries, available: imageCatalogAvailable } = imageCatalogState;
  const labImageConfig = useMemo(() => ({
    image: lab.containerConfig?.image || null,
    imageKey: lab.containerConfig?.imageKey || lab.imageKey || null,
  }), [lab.containerConfig, lab.imageKey]);
  const originalImageEntry = useMemo(
    () => (imageCatalogAvailable ? matchEntryForLab(imageEntries, labImageConfig) : null),
    [imageCatalogAvailable, imageEntries, labImageConfig]
  );
  // null means "untouched — follow whatever the lab says".
  const [imageChoice, setImageChoice] = useState(null);
  const selectedImageEntry = imageChoice || originalImageEntry;
  const imageChanged = Boolean(
    imageChoice && originalImageEntry && imageChoice.key !== originalImageEntry.key
  );

  const supportsDesktop = lab.containerConfig?.supportsDesktop === true;
  const supportsStudentDocker = lab.containerConfig?.supportsStudentDocker === true;
  // The server owns eligibility and authorization. A structured activity hint
  // keeps old API responses conservative; never infer teaching from a lab name,
  // course code, D2L requirement or Respondus availability.
  const runtimeEditable = lab.runtimeControls?.editable === true
    || (lab.runtimeControls?.editable === undefined && lab.activityType === 'lab');
  const runtimeControlsEditable = runtimeEditable && !imageChanged;
  const runtimeDisabledReason = lab.runtimeControls?.reason
    || 'Only explicitly classified teaching labs can use these features, not quizzes, midterms, finals, custom activities or unclassified legacy labs.';

  // Did the operator touch the memory buttons during THIS edit? It decides whether the
  // environment's own sizing is allowed to apply — see the comment on the save payload.
  const [memoryTouched, setMemoryTouched] = useState(false);

  // When the environment changes and nobody has overridden memory, let the catalogue's
  // sizing for that environment take effect by NOT sending memoryLimit at all. The server
  // fills only the resource fields a request leaves unset, so pinning memoryLimit here is
  // what would keep a Flutter lab on a Python-sized ceiling — and worse, a pinned ceiling
  // below the environment's memory REQUEST is rejected outright. Let the chosen profile
  // supply its ceiling unless the operator deliberately overrides it.
  const adoptsSuggestedMemory = shouldAdoptSuggestedMemory({
    imageChanged,
    memoryTouched,
    selectedEntry: selectedImageEntry,
    memoryLimit: formData.memoryLimit,
  });

  // Drop a pending choice whenever the form leaves edit mode or the lab reloads underneath us,
  // so a discarded edit never leaks into the next save.
  useEffect(() => {
    if (!isEditing) { setImageChoice(null); setMemoryTouched(false); }
  }, [isEditing, lab.id]);

  const updateMutation = useMutation({
    mutationFn: (data) => labs.update(labId, data),
    onSuccess: () => {
      onUpdate();
      setIsEditing(false);
    },
  });
  const facultyUpdate = updateMutation.data?.facultyPodConfigUpdate
    || updateMutation.data?.facultyPodImageUpdate;
  const facultyUpdateFailed = facultyUpdate
    && !['updated', 'already_current'].includes(facultyUpdate.status);

  const openLabMutation = useMutation({
    mutationFn: () => labs.open(labId),
    onSuccess: onUpdate,
  });

  const closeLabMutation = useMutation({
    mutationFn: () => labs.close(labId),
    onSuccess: onUpdate,
  });

  const handleSave = () => {
    const reminderPatch = d2lReminderPatch(formData.d2lSessionReminder, scheduleBaseline.current.d2lSessionReminder);
    const updateData = {
      ...schedulePatch(formData, scheduleBaseline.current),
      requireD2LAccess: formData.requireD2LAccess,
      ...(reminderPatch ? { d2lSessionReminder: reminderPatch } : {}),
      // Activity Rewind settings
      screenRecording: {
        enabled: formData.screenRecordingEnabled,
        intervalMs: parseInt(formData.screenRecordingIntervalMs) || 500,
      },
      allowWorkspaceDownload: formData.allowWorkspaceDownload,
      allowWorkspaceUpload: formData.allowWorkspaceUpload,
      clipboardPolicy: formData.clipboardPolicy,
      // Only for labs whose image serves apps, so every other lab's save is unchanged.
      ...(lab.containerConfig?.servesApps === true ? { allowAppPreview: formData.allowAppPreview } : {}),
      // Capabilities in the lab response are exact-image facts, not catalogue
      // guesses. Do not turn a blocked assessment's unrelated save into an
      // attempted feature enable, nor send any runtime capability fields.
      ...(supportsDesktop && runtimeControlsEditable ? { allowDesktop: formData.allowDesktop === true } : {}),
      ...(supportsStudentDocker && runtimeControlsEditable ? { allowStudentDocker: formData.allowStudentDocker === true } : {}),
      idleStop: { enabled: !formData.keepEnvironmentsRunning },
      // See buildContainerConfigUpdate: the environment is sent ONLY when a human
      // deliberately changed it, and always as the catalogue key.
      containerConfig: buildContainerConfigUpdate({
        memoryLimit: formData.memoryLimit,
        memoryTouched,
        imageChanged,
        selectedEntry: selectedImageEntry,
        adoptsSuggestedMemory,
      }),
    };
    updateMutation.mutate(updateData);
  };

  const handleCancel = () => {
    setFormData({
      status: lab.status || 'draft',
      openDate: toDateTimeLocal(lab.openDate),
      dueDate: toDateTimeLocal(lab.dueDate),
      closeDate: toDateTimeLocal(lab.closeDate),
      autoCloseOnDeadline: lab.autoCloseOnDeadline ?? true,
      gracePeriodMinutes: lab.gracePeriodMinutes ?? 0,
      requireD2LAccess: lab.requireD2LAccess ?? true,
      d2lSessionReminder: d2lReminderSettings(lab.d2lSessionReminder),
      screenRecordingEnabled: lab.screenRecording?.enabled ?? true,
      screenRecordingIntervalMs: lab.screenRecording?.intervalMs ?? 500,
      allowWorkspaceDownload: lab.allowWorkspaceDownload ?? false,
      allowWorkspaceUpload: lab.allowWorkspaceUpload ?? false,
      clipboardPolicy: lab.clipboardPolicy || 'allow',
      allowAppPreview: lab.allowAppPreview === true,
      allowDesktop: lab.allowDesktop === true,
      allowStudentDocker: lab.allowStudentDocker === true,
      keepEnvironmentsRunning: lab.idleStop?.enabled === false,
      memoryLimit: lab.containerConfig?.memoryLimit || '3Gi',
    });
    setImageChoice(null);
    setMemoryTouched(false);
    setIsEditing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'var(--accent-success)';
      case 'closed': return 'var(--accent-danger)';
      case 'draft': return 'var(--accent-warning)';
      default: return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft';
  };

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="card-header">
        <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={20} />
          Lab Settings
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isEditing && (
            <>
              {lab.status === 'draft' && (
                <button 
                  className="btn btn-success btn-sm"
                  onClick={() => openLabMutation.mutate()}
                  disabled={openLabMutation.isPending}
                >
                  {openLabMutation.isPending ? 'Opening...' : 'Open Lab'}
                </button>
              )}
              {lab.status === 'open' && (
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => closeLabMutation.mutate()}
                  disabled={closeLabMutation.isPending}
                >
                  {closeLabMutation.isPending ? 'Closing...' : 'Close Lab'}
                </button>
              )}
              {lab.status === 'closed' && (
                <button 
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    if (window.confirm('Reopen this lab? The close date will be cleared - you can set a new one in Edit Settings.')) {
                      openLabMutation.mutate();
                    }
                  }}
                  disabled={openLabMutation.isPending}
                >
                  {openLabMutation.isPending ? 'Reopening...' : 'Reopen Lab'}
                </button>
              )}
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => setIsEditing(true)}
              >
                Edit Settings
              </button>
            </>
          )}
          {isEditing && (
            <>
              <button 
                className="btn btn-success btn-sm"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: 20 }}>
        {/* A rejected save used to be silent: the form simply stayed in edit mode. The
            server now validates the environment and its sizing together, so the reason
            has to reach the person who can act on it. */}
        {updateMutation.isError && (
          <div role="alert" className="dlg-alert" style={{ marginBottom: 20 }}>
            {updateMutation.error?.response?.data?.message
              || updateMutation.error?.response?.data?.error
              || 'Failed to save these settings.'}
          </div>
        )}
        {updateMutation.isSuccess && facultyUpdateFailed && (
          <div role="alert" className="dlg-alert" style={{ marginBottom: 20 }}>
            Lab settings were saved, but the faculty environment could not apply them.
            {' '}Open Edit Settings and save again to retry.
            {facultyUpdate.error && <div>{facultyUpdate.error}</div>}
          </div>
        )}
        {updateMutation.isSuccess && updateMutation.data?.warning && (
          <div role="status" className="dlg-alert" style={{ marginBottom: 20 }}>
            {updateMutation.data.warning}
          </div>
        )}

        {/* Status */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ 
            display: 'block', 
            fontWeight: 500, 
            marginBottom: 8, 
            fontSize: 14 
          }}>
            Status
          </label>
          <div style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 8,
            padding: '8px 12px',
            background: 'var(--bg-tertiary)',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            color: getStatusColor(lab.status),
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: getStatusColor(lab.status),
            }} />
            {getStatusLabel(lab.status)}
          </div>
        </div>

        {/* Dates Grid */}
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>
          Times are shown in {Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local timezone'}.
        </p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 16,
          marginBottom: 20,
        }}>
          {/* Open Date */}
          <div>
            <label style={{ 
              display: 'block', 
              fontWeight: 500, 
              marginBottom: 8, 
              fontSize: 14 
            }}>
              Open Date
            </label>
            {isEditing ? (
              <input
                type="datetime-local"
                aria-label="Open Date"
                value={formData.openDate}
                onChange={(e) => setFormData({ ...formData, openDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                }}
              />
            ) : (
              <div style={{ 
                padding: '8px 12px',
                background: 'var(--bg-tertiary)',
                borderRadius: 6,
                fontSize: 14,
              }}>
                {lab.openDate ? new Date(lab.openDate).toLocaleString() : 'Not set'}
              </div>
            )}
          </div>

          {/* Due Date */}
          <div>
            <label style={{ 
              display: 'block', 
              fontWeight: 500, 
              marginBottom: 8, 
              fontSize: 14 
            }}>
              Due Date
            </label>
            {isEditing ? (
              <input
                type="datetime-local"
                aria-label="Due Date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                }}
              />
            ) : (
              <div style={{ 
                padding: '8px 12px',
                background: 'var(--bg-tertiary)',
                borderRadius: 6,
                fontSize: 14,
              }}>
                {lab.dueDate ? new Date(lab.dueDate).toLocaleString() : 'Not set'}
              </div>
            )}
          </div>

          {/* Close Date */}
          <div>
            <label style={{ 
              display: 'block', 
              fontWeight: 500, 
              marginBottom: 8, 
              fontSize: 14 
            }}>
              Close Date
            </label>
            {isEditing ? (
              <input
                type="datetime-local"
                aria-label="Close Date"
                value={formData.closeDate}
                onChange={(e) => setFormData({ ...formData, closeDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                }}
              />
            ) : (
              <div style={{ 
                padding: '8px 12px',
                background: 'var(--bg-tertiary)',
                borderRadius: 6,
                fontSize: 14,
              }}>
                {lab.closeDate ? new Date(lab.closeDate).toLocaleString() : 'Not set'}
              </div>
            )}
          </div>
        </div>

        {/* Lab Behavior Settings */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 20,
          marginBottom: 20,
        }}>
          {/* Left Column: Deadline Settings */}
          <div style={{
            padding: 16,
            background: 'var(--bg-tertiary)',
            borderRadius: 8,
            border: '1px solid var(--border-primary)',
          }}>
            <h4 style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              marginBottom: 16,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Deadline Settings
            </h4>
            
            {/* Auto-close toggle */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10,
                cursor: isEditing ? 'pointer' : 'default',
                fontSize: 14,
              }}>
                <input
                  type="checkbox"
                  checked={formData.autoCloseOnDeadline}
                  onChange={(e) => isEditing && setFormData({ ...formData, autoCloseOnDeadline: e.target.checked })}
                  disabled={!isEditing}
                  style={{ 
                    cursor: isEditing ? 'pointer' : 'default',
                    width: 18,
                    height: 18,
                  }}
                />
                <span>Auto-close on deadline</span>
              </label>
              <p style={{ 
                fontSize: 12, 
                color: 'var(--text-muted)', 
                marginTop: 6,
                marginLeft: 28,
              }}>
                Automatically close lab when close date is reached
              </p>
            </div>

            {/* Grace Period */}
            <div>
              <label style={{ 
                display: 'block', 
                fontWeight: 500, 
                marginBottom: 8, 
                fontSize: 14 
              }}>
                Grace Period
              </label>
              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min="0"
                    value={formData.gracePeriodMinutes}
                    onChange={(e) => setFormData({ ...formData, gracePeriodMinutes: e.target.value })}
                    style={{
                      width: 80,
                      padding: '8px 12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      color: 'var(--text-primary)',
                      fontSize: 14,
                      textAlign: 'center',
                    }}
                  />
                  <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>minutes</span>
                </div>
              ) : (
                <div style={{ 
                  padding: '8px 12px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 6,
                  fontSize: 14,
                  display: 'inline-block',
                }}>
                  {lab.gracePeriodMinutes || 0} minutes
                </div>
              )}
              <p style={{ 
                fontSize: 12, 
                color: 'var(--text-muted)', 
                marginTop: 6,
              }}>
                Extra time after deadline before auto-close
              </p>
            </div>
          </div>

          {/* Right Column: Access Control */}
          <div style={{
            padding: 16,
            background: 'var(--bg-tertiary)',
            borderRadius: 8,
            border: '1px solid var(--border-primary)',
          }}>
            <h4 style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              marginBottom: 16,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Access Control
            </h4>
            
            {/* D2L Security */}
            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10,
                cursor: isEditing ? 'pointer' : 'default',
                fontSize: 14,
              }}>
                <input
                  type="checkbox"
                  checked={formData.requireD2LAccess}
                  onChange={(e) => isEditing && setFormData({ ...formData, requireD2LAccess: e.target.checked })}
                  disabled={!isEditing}
                  style={{ 
                    cursor: isEditing ? 'pointer' : 'default',
                    width: 18,
                    height: 18,
                  }}
                />
                <span>Require D2L Access</span>
              </label>
              <p style={{ 
                fontSize: 12, 
                color: 'var(--text-muted)', 
                marginTop: 6,
                marginLeft: 28,
              }}>
                Students must access the lab through D2L LMS link
              </p>
              {formData.requireD2LAccess && (
                <div style={{
                  marginTop: 12,
                  marginLeft: 28,
                  padding: '8px 12px',
                  background: 'var(--badge-info-bg)',
                  border: '1px solid var(--accent-primary)',
                  borderRadius: 6,
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                }}>
                  <strong style={{ color: 'var(--accent-primary)' }}>🔒 Secure Mode:</strong> Direct URL access is blocked
                </div>
              )}
            </div>
            
            <D2LSessionReminderSettings
              idPrefix="settings"
              value={formData.d2lSessionReminder}
              onChange={change => setFormData(current => ({ ...current,
                d2lSessionReminder: { ...current.d2lSessionReminder, ...change } }))}
              disabled={!isEditing}
            />

            {/* D2L Shareable Link */}
            <D2LShareableLink labId={labId} labName={lab.name} requireD2LAccess={formData.requireD2LAccess} />
          </div>
        </div>

        {/* Activity Monitoring Section */}
        <div style={{
          padding: 16,
          background: 'var(--bg-tertiary)',
          borderRadius: 8,
          border: '1px solid var(--border-primary)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h4 style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <Rewind size={16} />
              Activity Monitoring
            </h4>
            <span style={{
              fontSize: 11,
              padding: '4px 8px',
              background: formData.screenRecordingEnabled ? 'var(--badge-success-bg)' : 'var(--badge-danger-bg)',
              color: formData.screenRecordingEnabled ? 'var(--accent-success)' : 'var(--accent-danger)',
              borderRadius: 4,
              fontWeight: 500,
            }}>
              {formData.screenRecordingEnabled ? '● Recording Enabled' : '○ Recording Disabled'}
            </span>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24 }}>
            {/* Screen Recording Toggle */}
            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10,
                cursor: isEditing ? 'pointer' : 'default',
                fontSize: 14,
              }}>
                <input
                  type="checkbox"
                  checked={formData.screenRecordingEnabled}
                  onChange={(e) => isEditing && setFormData({ ...formData, screenRecordingEnabled: e.target.checked })}
                  disabled={!isEditing}
                  style={{ 
                    cursor: isEditing ? 'pointer' : 'default',
                    width: 18,
                    height: 18,
                  }}
                />
                <span>Enable Screen Recording</span>
              </label>
              <p style={{ 
                fontSize: 12, 
                color: 'var(--text-muted)', 
                marginTop: 6,
                marginLeft: 28,
              }}>
                Capture periodic screenshots for activity playback and review
              </p>
            </div>

            {/* Screenshot Interval */}
            <div style={{ opacity: formData.screenRecordingEnabled ? 1 : 0.5 }}>
              <label style={{ 
                display: 'block', 
                fontWeight: 500, 
                marginBottom: 8, 
                fontSize: 14 
              }}>
                Screenshot Interval
              </label>
              <select
                value={formData.screenRecordingIntervalMs}
                onChange={(e) => isEditing && setFormData({ ...formData, screenRecordingIntervalMs: parseInt(e.target.value) })}
                disabled={!isEditing || !formData.screenRecordingEnabled}
                className="form-input"
                style={{ 
                  padding: '8px 12px', 
                  fontSize: 14,
                  width: '100%',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: 'var(--text-primary)',
                }}
              >
                <option value={250}>250ms (4 fps) - High detail</option>
                <option value={500}>500ms (2 fps) - Default</option>
                <option value={1000}>1 second - Balanced</option>
                <option value={2000}>2 seconds - Lower storage</option>
                <option value={5000}>5 seconds - Minimal</option>
              </select>
              <p style={{ 
                fontSize: 12, 
                color: 'var(--text-muted)', 
                marginTop: 6,
              }}>
                {formData.screenRecordingIntervalMs <= 500 
                  ? '~280MB/hour • Best for short labs (< 3.5 hrs)'
                  : formData.screenRecordingIntervalMs <= 1000
                  ? '~140MB/hour • Good for medium labs (< 7 hrs)'
                  : '~70MB/hour • Best for long labs (> 7 hrs)'
                }
              </p>
              <p style={{
                fontSize: 12,
                color: 'var(--text-muted)',
                marginTop: 4,
              }}>
                While a student types, each screenshot waits for a short pause (8 seconds at most), and in heavier
                editors (INFS2101, INFS4102) screenshots are spaced further apart, so recording never holds back their
                typing. Every keystroke is still logged.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Workspace Imports Section. Styled exactly like Workspace Download below:
          index.css gives every <input> width:100% and padding, so a checkbox
          without an explicit size stretches across half the row and floats. */}
      <div style={{ padding: 16, background: 'var(--bg-tertiary)', borderRadius: 8, border: '1px solid var(--border-primary)', marginBottom: 16 }}>
        <h4 style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 16,
        }}>
          Workspace Imports
        </h4>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: isEditing ? 'pointer' : 'default',
          fontSize: 14,
        }}>
          <input
            type="checkbox"
            checked={formData.allowWorkspaceUpload}
            onChange={(e) => isEditing && setFormData({ ...formData, allowWorkspaceUpload: e.target.checked })}
            disabled={!isEditing}
            style={{
              cursor: isEditing ? 'pointer' : 'default',
              width: 18,
              height: 18,
              marginTop: 2,
              flexShrink: 0,
            }}
          />
          <div>
            <span>Allow students to import existing code</span>
            <p style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              marginTop: 6,
            }}>
              Students upload files, a local folder, or a ZIP into a separate project. Imports are recorded with the
              student's activity. You can always upload into a student's IDE yourself; those uploads are recorded too.
            </p>
          </div>
        </label>
      </div>

      {/* Copy and paste in the student IDE */}
      <div style={{
        padding: 16,
        background: 'var(--bg-tertiary)',
        borderRadius: 8,
        border: '1px solid var(--border-primary)',
        marginBottom: 16,
      }}>
        <h4 style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 4,
        }}>
          Copy and Paste
        </h4>
        <ClipboardPolicyField
          name="lab-settings-clipboard"
          quietLegend
          value={formData.clipboardPolicy}
          onChange={(value) => isEditing && setFormData({ ...formData, clipboardPolicy: value })}
          disabled={!isEditing}
          note="A change reaches IDEs that are already open within a minute."
        />
      </div>

      {/* Students' own web apps (images that serve them, e.g. INFS4102) */}
      {lab.containerConfig?.servesApps === true && (
        <div style={{
          padding: 16,
          background: 'var(--bg-tertiary)',
          borderRadius: 8,
          border: '1px solid var(--border-primary)',
          marginBottom: 16,
        }}>
          <h4 style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 16,
          }}>
            Apps in the Browser
          </h4>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            cursor: isEditing ? 'pointer' : 'default',
            fontSize: 14,
          }}>
            <input
              type="checkbox"
              checked={formData.allowAppPreview}
              onChange={(e) => isEditing && setFormData({ ...formData, allowAppPreview: e.target.checked })}
              disabled={!isEditing}
              style={{
                cursor: isEditing ? 'pointer' : 'default',
                width: 18,
                height: 18,
                marginTop: 2,
                flexShrink: 0,
              }}
            />
            <div>
              <span>Let students open their apps in the browser</span>
              <p style={{
                fontSize: 12,
                color: 'var(--text-muted)',
                marginTop: 6,
              }}>
                Students open a web app they run, such as a Spring Boot API with Swagger UI or a Flutter web build,
                in a new tab with Open app in their IDE. You can open a student's app the same way from their IDE.
                Turn this off for exams: an open app can also run a second, unrestricted editor. Turning it off
                closes every app already open.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Opt-in teaching runtimes. The ordinary/default image shows nothing. */}
      {(supportsDesktop || supportsStudentDocker) && (
        <div style={{
          padding: 16,
          background: 'var(--bg-tertiary)',
          borderRadius: 8,
          border: '1px solid var(--border-primary)',
          marginBottom: 16,
        }}>
          <h4 style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 12,
          }}>
            Teaching Runtime Tools
          </h4>
          <p id="lab-runtime-tools-note" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Off by default and not for locked assessments. These server-side tools can run programs outside the
            editor's restrictions; Respondus is not a security boundary for them. Enabling either requires
            administrator support and restarting student environments; workspace files are retained.
          </p>
          {!runtimeEditable && (
            <p id="lab-runtime-tools-disabled" role="status" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              {runtimeDisabledReason}
              {(formData.allowDesktop || formData.allowStudentDocker) && (
                <>{' '}An enabled choice is still saved on this record, but runtime access is denied by the current policy.
                  This form leaves those saved choices unchanged while tools are unavailable; ask an administrator to clear them.</>
              )}
            </p>
          )}
          {imageChanged && (
            <p id="lab-runtime-tools-image-change" role="status" style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Save the environment change first. Runtime tools can be enabled only after the server confirms
              the saved image's capabilities; this save leaves the existing runtime choices unchanged.
            </p>
          )}
          {[
            supportsDesktop && {
              field: 'allowDesktop', title: 'Allow students to use the embedded Linux desktop',
              description: 'Open desktop stays in the existing IDE tab for Chrome and Flutter Linux apps, with Return to editor always available. Desktop recording follows the lab recording settings. System clipboard integration is disabled.'
            },
            supportsStudentDocker && {
              field: 'allowStudentDocker', title: 'Allow students to use Docker, Buildx and Compose',
              description: 'Uses a dedicated per-student Docker daemon only in the administrator-approved Kata sandbox. No host Docker socket or ordinary privileged student container is permitted. Stop enabled environments before disabling Docker.'
            },
          ].filter(Boolean).map(({ field, title, description }) => (
            <div key={field} style={{ marginTop: 12 }}>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14,
                cursor: isEditing && runtimeControlsEditable ? 'pointer' : 'default',
              }}>
                <input
                  type="checkbox"
                  checked={formData[field] === true}
                  disabled={!isEditing || !runtimeControlsEditable}
                  aria-describedby={`lab-runtime-tools-note${!runtimeEditable ? ' lab-runtime-tools-disabled' : ''}${imageChanged ? ' lab-runtime-tools-image-change' : ''}`}
                  onChange={(e) => isEditing && runtimeControlsEditable && setFormData({ ...formData, [field]: e.target.checked })}
                  style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }}
                />
                <div>
                  <span>{title}</span>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{description}</p>
                </div>
              </label>
            </div>
          ))}
        </div>
      )}

      {/* Workspace Download Section */}
      <div style={{
        padding: 16,
        background: 'var(--bg-tertiary)',
        borderRadius: 8,
        border: '1px solid var(--border-primary)',
      }}>
        <h4 style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          Workspace Download
        </h4>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: isEditing ? 'pointer' : 'default',
          fontSize: 14,
        }}>
          <input
            type="checkbox"
            checked={formData.allowWorkspaceDownload}
            onChange={(e) => isEditing && setFormData({ ...formData, allowWorkspaceDownload: e.target.checked })}
            disabled={!isEditing}
            style={{
              cursor: isEditing ? 'pointer' : 'default',
              width: 18,
              height: 18,
              marginTop: 2,
            }}
          />
          <div>
            <span>Allow students to download workspace as ZIP</span>
            <p style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              marginTop: 6,
            }}>
              {formData.allowWorkspaceDownload
                ? 'A "Download Work" button will appear in the IDE status bar. Students can download their workspace files as a ZIP for LMS submission.'
                : 'Download button is hidden. Students cannot export their workspace.'}
              {' '}Changes take effect when student environments are created or recreated.
            </p>
          </div>
        </label>
      </div>

      {/* Idle pause */}
      <div style={{
        padding: 16,
        background: 'var(--bg-tertiary)',
        borderRadius: 8,
        border: '1px solid var(--border-primary)',
      }}>
        <h4 style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 16,
        }}>
          Idle Environments
        </h4>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: isEditing ? 'pointer' : 'default',
          fontSize: 14,
        }}>
          <input
            type="checkbox"
            checked={formData.keepEnvironmentsRunning}
            onChange={(e) => isEditing && setFormData({ ...formData, keepEnvironmentsRunning: e.target.checked })}
            disabled={!isEditing}
            style={{
              cursor: isEditing ? 'pointer' : 'default',
              width: 18,
              height: 18,
              marginTop: 2,
            }}
          />
          <div>
            <span>Keep environments running</span>
            <p style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              marginTop: 6,
            }}>
              {formData.keepEnvironmentsRunning
                ? 'Environments stay up however long they sit unused. Use this for exams, or anything that must not be interrupted.'
                : 'An environment unused for a long time is paused; its files are kept, and it starts again when the student opens the IDE (about a minute).'}
            </p>
          </div>
        </label>
      </div>

      {/* Lab environment. Sits directly above memory because the two are one decision:
          the environment determines the floor, the memory limit sets the ceiling, and
          picking a heavy toolchain without raising the ceiling is how sessions get killed. */}
      <LabImageCard
        entries={imageEntries}
        available={imageCatalogAvailable}
        isLoading={imageCatalogState.isLoading}
        unavailableReason={imageCatalogState.unavailableReason}
        selected={selectedImageEntry}
        original={originalImageEntry}
        onSelect={(entry) => { if (isEditing) setImageChoice(entry); }}
        isEditing={isEditing}
        memoryLimit={formData.memoryLimit}
        adoptsSuggestedMemory={adoptsSuggestedMemory}
      />

      {/* Memory per student and faculty pod.
          Demand is strongly bimodal — a Python lab sits around 90 MiB while a
          Java lab idles near 2 GiB, because the language server alone takes
          ~1.5 GiB. A single platform-wide limit is simultaneously far too
          generous for most labs and too tight for the ones that need it, which
          is what was killing student sessions mid-work. */}
      <div className="card" style={{ padding: 20, marginTop: 16 }}>
        <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 15 }}>
          <Cpu size={16} aria-hidden="true" /> Memory per environment
        </h4>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px' }}>
          Choosing a size reserves it in full for each student environment, so a busy server can't
          squeeze it, and caps what the environment can use; the faculty IDE shares the cap.
          Exceeding it ends the session, so allow room for whatever they actually run, not just the editor.
        </p>

        <div role="group" aria-label="Memory limit" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            { value: '2Gi', label: '2 GiB', hint: 'Python, web, shell' },
            { value: '3Gi', label: '3 GiB', hint: 'Default — safe for most' },
            { value: '4Gi', label: '4 GiB', hint: 'Java, C++, heavy tooling' },
            { value: '6Gi', label: '6 GiB', hint: 'Data science, containers' },
            { value: '8Gi', label: '8 GiB', hint: 'INFS4102 — Flutter and Spring Boot' },
          ].map((opt) => {
            const active = formData.memoryLimit === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={active}
                disabled={!isEditing}
                title={opt.hint}
                onClick={() => {
                  if (!isEditing) return;
                  setMemoryTouched(true);
                  setFormData({ ...formData, memoryLimit: opt.value });
                }}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left',
                  padding: '9px 14px', borderRadius: 8, fontFamily: 'inherit',
                  cursor: isEditing ? 'pointer' : 'default',
                  background: active ? 'var(--badge-info-bg)' : 'var(--bg-tertiary)',
                  color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                  opacity: !isEditing && !active ? 0.55 : 1,
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{opt.label}</span>
                <span style={{ fontSize: 11, opacity: 0.8 }}>{opt.hint}</span>
              </button>
            );
          })}
        </div>

        {formData.memoryLimit === '2Gi' && (
          <p role="status" style={{
            display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 12, marginBottom: 0,
            fontSize: 12.5, color: 'var(--accent-warning)',
          }}>
            <AlertTriangle size={13} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
            A Java lab idles at roughly 1.9 GiB before the student runs anything. At 2 GiB
            their session will be terminated the first time they run their program.
          </p>
        )}

        {adoptsSuggestedMemory && (
          <p role="status" style={{ fontSize: 12.5, color: 'var(--accent-primary)', marginTop: 12, marginBottom: 0 }}>
            Saving will set this to {formatMemory(selectedImageEntry.suggestedMemory)} — what the
            environment you picked above needs. Choose a value here to override it.
          </p>
        )}

        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, marginBottom: 0 }}>
          Saving a changed limit restarts the faculty environment to apply it. Student environments
          receive it when created; existing student environments keep their current limit until recreated.
        </p>
      </div>
    </div>
  );
}

// Faculty Pod Section Component
function FacultyPodSection({ labId, lab, onUpdate }) {
  const queryClient = useQueryClient();
  const [showGrading, setShowGrading] = useState(false);

  // Query faculty pod status
  const { data: podStatus, isLoading: podLoading, refetch: refetchPod } = useQuery({
    queryKey: ['faculty-pod', labId],
    queryFn: () => facultyPod.get(labId),
    enabled: !!labId,
    refetchInterval: lab?.facultyPod?.status === 'creating' ? 5000 : false,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: () => facultyPod.create(labId),
    onSuccess: () => {
      refetchPod();
      onUpdate();
    },
    onError: (err) => alert(`Failed to create faculty pod: ${err.response?.data?.error || err.message}`),
  });

  const startMutation = useMutation({
    mutationFn: () => facultyPod.start(labId),
    onSuccess: () => {
      refetchPod();
      onUpdate();
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => facultyPod.stop(labId),
    onSuccess: () => {
      refetchPod();
      onUpdate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (deletePvc) => facultyPod.delete(labId, deletePvc),
    onSuccess: () => {
      refetchPod();
      onUpdate();
    },
  });

  const snapshotMutation = useMutation({
    mutationFn: () => facultyPod.triggerSnapshot(labId),
    onSuccess: (data) => {
      alert(`Snapshot complete: ${data.snapshotted} succeeded, ${data.failed} failed`);
      onUpdate();
    },
    onError: (err) => alert(`Snapshot failed: ${err.response?.data?.error || err.message}`),
  });

  const cleanupMutation = useMutation({
    mutationFn: () => facultyPod.cleanupSnapshots(labId),
    onSuccess: (data) => {
      alert(`Cleanup complete: freed ${data.freed}`);
      refetchPod();
    },
    onError: (err) => alert(`Cleanup failed: ${err.response?.data?.error || err.message}`),
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'var(--accent-success)';
      case 'creating': return 'var(--accent-warning)';
      case 'stopped': return 'var(--text-muted)';
      case 'error': return 'var(--accent-danger)';
      default: return 'var(--text-muted)';
    }
  };

  const isKubernetes = true; // Assume Kubernetes for now since it's the primary target

  if (podLoading) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <GraduationCap size={20} />
            Faculty Pod
          </h2>
        </div>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  const hasActivePod = podStatus?.enabled && podStatus?.status !== 'not_created' && podStatus?.status !== 'deleted';

  return (
    <>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
            <GraduationCap size={20} />
            Faculty Pod
            {hasActivePod && (
              <span style={{
                marginLeft: 8,
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 500,
                background: `${getStatusColor(podStatus.status)}20`,
                color: getStatusColor(podStatus.status),
              }}>
                {podStatus.status || 'Unknown'}
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {hasActivePod && (
              <>
                {podStatus.status === 'running' && (
                  <>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => snapshotMutation.mutate()}
                      disabled={snapshotMutation.isPending}
                      title="Take snapshot of all running student containers"
                    >
                      {snapshotMutation.isPending ? (
                        <div className="spinner" style={{ width: 14, height: 14 }} />
                      ) : (
                        <Camera size={14} />
                      )}
                      Snapshot All
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowGrading(true)}
                    >
                      <Star size={14} />
                      Grading
                    </button>
                  </>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => refetchPod()}
                  title="Refresh status"
                >
                  <RefreshCw size={14} />
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ padding: 20 }}>
          {!hasActivePod ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <GraduationCap size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
              <p style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                Faculty Pod provides a persistent workspace for preparing labs and grading student work.
              </p>
              <p style={{ marginBottom: 16, fontSize: 12, color: 'var(--text-muted)' }}>
                Includes snapshot storage for periodic backups of student work.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || !isKubernetes}
              >
                {createMutation.isPending ? (
                  <>
                    <div className="spinner" style={{ width: 16, height: 16 }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Create Faculty Pod
                  </>
                )}
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              {/* Status Card */}
              <div style={{
                padding: 16,
                background: 'var(--bg-tertiary)',
                borderRadius: 8,
                border: '1px solid var(--border-primary)',
              }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Status</div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 16,
                  fontWeight: 500,
                  color: getStatusColor(podStatus.status),
                }}>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: getStatusColor(podStatus.status),
                    animation: podStatus.status === 'creating' ? 'pulse 2s infinite' : 'none',
                  }} />
                  {podStatus.status?.charAt(0).toUpperCase() + podStatus.status?.slice(1)}
                </div>
                {podStatus.status === 'stopped' && podStatus.stopReason === 'lab_closed' && (
                  <p style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                    Stopped automatically some time after the lab closed. Grading and the snapshot list
                    still work; start the pod to open its IDE and browse snapshot files.
                  </p>
                )}
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  {podStatus.status === 'running' ? (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => stopMutation.mutate()}
                      disabled={stopMutation.isPending}
                    >
                      {stopMutation.isPending ? (
                        <div className="spinner" style={{ width: 12, height: 12 }} />
                      ) : (
                        <Square size={12} />
                      )}
                      Stop
                    </button>
                  ) : podStatus.status === 'stopped' ? (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => startMutation.mutate()}
                      disabled={startMutation.isPending}
                    >
                      {startMutation.isPending ? (
                        <div className="spinner" style={{ width: 12, height: 12 }} />
                      ) : (
                        <Play size={12} />
                      )}
                      Start
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Storage Card */}
              <div style={{
                padding: 16,
                background: 'var(--bg-tertiary)',
                borderRadius: 8,
                border: '1px solid var(--border-primary)',
              }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span><HardDrive size={12} style={{ marginRight: 4 }} />Snapshot Storage</span>
                  {podStatus.storageUsage && podStatus.storageUsage.usagePercent >= 60 && (
                    <button
                      className="btn btn-sm"
                      style={{ fontSize: 10, padding: '2px 6px', color: 'var(--accent-danger)', border: '1px solid var(--accent-danger)', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                      onClick={() => {
                        if (window.confirm('Clean up old snapshots? This will keep only the latest 2 snapshots per student.')) {
                          cleanupMutation.mutate();
                        }
                      }}
                      disabled={cleanupMutation.isPending}
                    >
                      {cleanupMutation.isPending ? <div className="spinner" style={{ width: 8, height: 8 }} /> : <Trash2 size={10} />}
                      Clean Up
                    </button>
                  )}
                </div>
                {podStatus.storageUsage ? (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 500 }}>
                      {podStatus.storageUsage.used} / {podStatus.storageUsage.total}
                    </div>
                    <div style={{ marginTop: 8, background: 'var(--bg-primary)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                      <div style={{
                        width: `${podStatus.storageUsage.usagePercent}%`,
                        height: '100%',
                        borderRadius: 4,
                        background: podStatus.storageUsage.usagePercent >= 85 ? 'var(--accent-danger)' : podStatus.storageUsage.usagePercent >= 60 ? 'var(--accent-warning)' : 'var(--accent-success)',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {podStatus.storageUsage.usagePercent}% used — {podStatus.storageUsage.available} available
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 16, fontWeight: 500 }}>
                    {podStatus.storageSize || '50Gi'}
                  </div>
                )}
              </div>

              {/* Access Card */}
              {podStatus.status === 'running' && podStatus.url && (
                <div style={{
                  padding: 16,
                  background: 'var(--bg-tertiary)',
                  borderRadius: 8,
                  border: '1px solid var(--border-primary)',
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Faculty IDE</div>
                  <a
                    href={podStatus.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <ExternalLink size={14} />
                    Open IDE
                  </a>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                    Browse snapshots in /snapshots/ folder
                  </p>
                </div>
              )}

              {/* Snapshot Config Card */}
              <div style={{
                padding: 16,
                background: 'var(--bg-tertiary)',
                borderRadius: 8,
                border: '1px solid var(--border-primary)',
              }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                  <Clock size={12} style={{ marginRight: 4 }} />
                  Auto-Snapshot
                </div>
                <div style={{ fontSize: 14 }}>
                  {lab?.isSemesterLab ? (
                    <span style={{ color: 'var(--text-muted)' }}>On-demand only</span>
                  ) : (
                    <span>Every {podStatus.snapshotConfig?.intervalMinutes || 15} minutes</span>
                  )}
                </div>
                {podStatus.snapshotConfig?.lastSnapshotAt && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Last: {formatDistanceToNow(new Date(podStatus.snapshotConfig.lastSnapshotAt), { addSuffix: true })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Delete Option */}
          {hasActivePod && (
            <div style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: '1px solid var(--border-primary)',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => {
                  const deleteSnapshots = window.confirm(
                    'Delete faculty pod?\n\nClick OK to also delete all snapshots, or Cancel to keep snapshots.'
                  );
                  if (window.confirm(`Are you sure? This will ${deleteSnapshots ? 'delete all snapshots and ' : ''}remove the faculty pod.`)) {
                    deleteMutation.mutate(deleteSnapshots);
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <div className="spinner" style={{ width: 14, height: 14 }} />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete Faculty Pod
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Grading Modal */}
      {showGrading && (
        <GradingPanel
          labId={labId}
          lab={lab}
          onClose={() => setShowGrading(false)}
        />
      )}
    </>
  );
}

// Grading Panel Component
function GradingPanel({ labId, lab, onClose }) {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [gradeInput, setGradeInput] = useState('');
  const [feedbackInput, setFeedbackInput] = useState('');

  // Query grading data
  const { data: gradingData, isLoading, refetch } = useQuery({
    queryKey: ['grading-students', labId],
    queryFn: () => facultyPod.getGradingStudents(labId),
  });

  const gradeMutation = useMutation({
    mutationFn: ({ studentId, data }) => facultyPod.submitGrade(labId, studentId, data),
    onSuccess: () => {
      refetch();
      setGradeInput('');
      setFeedbackInput('');
      alert('Grade submitted successfully');
    },
    onError: (err) => alert(`Failed to submit grade: ${err.response?.data?.error || err.message}`),
  });

  const handleSubmitGrade = (studentId) => {
    if (!gradeInput.trim()) {
      alert('Please enter a grade');
      return;
    }
    gradeMutation.mutate({
      studentId,
      data: {
        grade: gradeInput,
        gradeNumeric: parseFloat(gradeInput) || null,
        feedback: feedbackInput,
      }
    });
  };

  const getGradingStatusBadge = (status) => {
    switch (status) {
      case 'graded':
        return <span className="badge badge-success">Graded</span>;
      case 'in_review':
        return <span className="badge badge-warning">In Review</span>;
      default:
        return <span className="badge">Ungraded</span>;
    }
  };

  return (
    <Dialog size="xl" onClose={onClose} isDirty={gradeInput.trim() !== '' || feedbackInput.trim() !== ''}>
      <DialogHeader title={`Grading - ${lab?.name || 'Lab'}`} icon={<Star size={20} />} onClose={onClose} />

      <DialogBody style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', height: '70vh' }}>
            {/* Student List */}
            <div style={{
              width: 300,
              borderRight: '1px solid var(--border-primary)',
              overflow: 'auto',
            }}>
              <div style={{
                padding: 12,
                borderBottom: '1px solid var(--border-primary)',
                background: 'var(--bg-tertiary)',
                fontSize: 12,
                color: 'var(--text-muted)',
              }}>
                {gradingData?.summary?.graded || 0} / {gradingData?.summary?.total || 0} graded
              </div>
              {gradingData?.students?.map((student) => (
                <div
                  key={student.studentId}
                  onClick={() => setSelectedStudent(student)}
                  style={{
                    padding: 12,
                    borderBottom: '1px solid var(--border-primary)',
                    cursor: 'pointer',
                    background: selectedStudent?.studentId === student.studentId ? 'var(--bg-tertiary)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{student.name || student.studentId}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{student.studentId}</div>
                    </div>
                    {getGradingStatusBadge(student.latestSnapshot?.gradingStatus)}
                  </div>
                  {student.latestSnapshot?.grade && (
                    <div style={{ marginTop: 4, fontSize: 14, fontWeight: 600, color: 'var(--accent-primary)' }}>
                      Grade: {student.latestSnapshot.grade}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Grading Form */}
            <div style={{ flex: 1, padding: 20, overflow: 'auto' }}>
              {selectedStudent ? (
                <>
                  <h3 style={{ marginBottom: 16 }}>
                    {selectedStudent.name || selectedStudent.studentId}
                  </h3>

                  {selectedStudent.hasSnapshot ? (
                    <>
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                          Latest Snapshot
                        </div>
                        <div style={{ fontSize: 14 }}>
                          {selectedStudent.latestSnapshot?.timestamp
                            ? formatDistanceToNow(new Date(selectedStudent.latestSnapshot.timestamp), { addSuffix: true })
                            : 'N/A'}
                        </div>
                      </div>

                      {/* Current Grade */}
                      {selectedStudent.latestSnapshot?.grade && (
                        <div style={{
                          marginBottom: 16,
                          padding: 12,
                          background: 'var(--badge-success-bg)',
                          border: '1px solid var(--accent-success)',
                          borderRadius: 8,
                        }}>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Current Grade</div>
                          <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--accent-success)' }}>
                            {selectedStudent.latestSnapshot.grade}
                          </div>
                          {selectedStudent.latestSnapshot.feedback && (
                            <div style={{ marginTop: 8, fontSize: 14, color: 'var(--text-secondary)' }}>
                              <MessageSquare size={12} style={{ marginRight: 4 }} />
                              {selectedStudent.latestSnapshot.feedback}
                            </div>
                          )}
                          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                            Graded {selectedStudent.latestSnapshot.gradedAt
                              ? formatDistanceToNow(new Date(selectedStudent.latestSnapshot.gradedAt), { addSuffix: true })
                              : ''}
                          </div>
                        </div>
                      )}

                      {/* Grade Input */}
                      <Field label={selectedStudent.latestSnapshot?.grade ? 'Update Grade' : 'Enter Grade'}>
                        <input
                          type="text"
                          value={gradeInput}
                          onChange={(e) => setGradeInput(e.target.value)}
                          placeholder="e.g., 85, A-, B+"
                          style={{ width: '100%', marginBottom: 12 }}
                        />
                      </Field>

                      {/* Feedback Input */}
                      <Field label="Feedback (optional)">
                        <textarea
                          value={feedbackInput}
                          onChange={(e) => setFeedbackInput(e.target.value)}
                          placeholder="Enter feedback for the student..."
                          rows={4}
                          style={{ width: '100%', resize: 'vertical' }}
                        />
                      </Field>

                      <button
                        className="btn btn-primary"
                        onClick={() => handleSubmitGrade(selectedStudent.studentId)}
                        disabled={gradeMutation.isPending}
                      >
                        {gradeMutation.isPending ? (
                          <>
                            <div className="spinner" style={{ width: 16, height: 16 }} />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={16} />
                            Submit Grade
                          </>
                        )}
                      </button>

                      <div style={{
                        marginTop: 20,
                        padding: 12,
                        background: 'var(--bg-tertiary)',
                        borderRadius: 8,
                        fontSize: 12,
                        color: 'var(--text-muted)',
                      }}>
                        <strong>Tip:</strong> Open the Faculty IDE to browse student snapshots in /snapshots/{selectedStudent.studentId}/
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      <AlertCircle size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                      <p>No snapshot available for this student.</p>
                      <p style={{ fontSize: 12, marginTop: 8 }}>
                        Snapshots are taken periodically while the lab is open.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  <Star size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
                  <p>Select a student from the list to grade their work.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </DialogFooter>
    </Dialog>
  );
}

export default LabDetail;

